import { z } from 'zod';
import { TEAM_IDS } from '../constants/teams.js';
import { SPIKE_FEED_COUNT } from '../constants/simulation.js';
import { TEAM_MERGE_MAX_MEMBERS } from '../constants/teams.js';
import { DEATHMATCH_STANDINGS } from '../constants/deathmatch.js';
import { ROOM_MODES } from '../constants/room-modes.js';
import { ROOM_NAME_MAX_LENGTH } from '../constants/room-names.js';
import {
  MAX_CELLS_PER_PLAYER,
  MAX_WORLD_SIZE,
  PELLET_SIZE_STEPS,
  POWER_UP_INVENTORY_SIZE,
  ROOM_MAX_PLAYERS,
  STAMINA_MAX,
} from '../constants/simulation.js';
import { MAX_MINI_GAME_CHOICE_LENGTH, MINI_GAME_POT_MAX_PERCENT } from '../minigames/minigame.js';
import { MAX_POWER_UP_ID_LENGTH } from '../powerups/powerup.js';
import { SAYING_TEXT_MAX_LENGTH } from '../constants/sayings.js';
import { displayNameSchema } from '../schemas/auth.js';
import { sayingIdSchema } from '../schemas/sayings.js';
import { MAX_NOTICES_PER_FRAME } from '../constants/notices.js';
import {
  MAX_VOTE_OPTION_ID_LENGTH,
  MAX_VOTE_OPTION_LABEL_LENGTH,
  MAX_VOTE_OPTIONS,
  MAX_VOTE_PROMPT_LENGTH,
  MIN_VOTE_OPTIONS,
} from '../constants/votes.js';
import type {
  CellSnapshot,
  DuelSnapshot,
  GameNotice,
  LeaderboardEntry,
  LeaderboardSelf,
  MatchPhase,
  NoticeActor,
  PelletSnapshot,
  PlayerCountry,
  PlayerSkin,
  PlayerSnapshot,
  PowerUpSnapshot,
  PrizeShardSnapshot,
  StarSnapshot,
  ProjectileSnapshot,
  RoomInfo,
  RosterEntry,
  SayingSnapshot,
  MergeSnapshot,
  SpikeSnapshot,
  StarOutcome,
  VoteOption,
  VoteResultSnapshot,
  VoteSelfSnapshot,
  VoteTally,
} from '../types/index.js';

/**
 * Bump on any change to a message shape. `hello` carries the client's version and the server
 * refuses a mismatch with a `protocol_version_mismatch` error rather than half-speaking an old
 * dialect — a stale browser tab must not be able to desync a room.
 *
 * 7 is the binary wire format (`protocol/binary-codec.ts`). It is a *representation* change and
 * not a shape change, but it has to be a version bump all the same: a field's position in the
 * byte stream is now its name, so a peer that does not have the encoder would not fail to find a
 * key — it would read the next field. The handshake refuses a v6 tab rather than letting it try.
 *
 * 9 adds the `split` action to the `input` frame — see `clientInputSchema.split`.
 *
 * 10 adds `self` to the `leaderboard` frame: the viewer's own rank and mass when they are
 * outside the top `LEADERBOARD_SIZE`, so a player at 37th can be shown their own place.
 *
 * 11 makes bots visible: `bots` on the `snapshot` and `delta` frames names which of the players
 * in view is an AI, and `humanCount` / `botCount` on the `leaderboard` frame carry the room's
 * split. This reverses a deliberate invariant — the wire used to say nothing at all about it, on
 * the reasoning that a client able to tell the two apart would play differently against each —
 * and it is reversed because the project owner asked for bots to be unmistakable in game.
 *
 * 12 adds country flags: `countries` on the `snapshot` and `delta` frames, on the same
 * first-appearance channel the skins ride, and an optional `country` on each `leaderboard`
 * entry. The value is resolved from the connecting request's IP by the API process and sealed
 * into the WebSocket ticket — it is never client-declared, for the same reason a skin URL is
 * never client-declared (see `WsTicket.country`).
 *
 * 13 adds the `eject` action to the `input` frame — see `clientInputSchema.eject`. It is the
 * third thing that rides the movement frame, beside `split` and `duelChoice`, and it adds no
 * field anywhere else: what it throws is an ordinary pellet, told apart from the room's own food
 * by its `size` alone (see PELLET_WIRE_MASS_MAX).
 *
 * 14 fills the reserved `powerup` replication tier. `powerUps` on the `snapshot` and `delta`
 * frames carries the room's glowing orbs — the whole set, not an interest-culled one, and only
 * on the ticks UPDATE_INTERVAL_TICKS.powerup names (`null` on every other frame, which is "no
 * news" rather than "none left"); `heldPowerUp` on both frames is the viewer's own inventory
 * slot; and `usePowerUp` on the `input` frame is the fourth action to ride the movement frame,
 * after `duelChoice`, `split` and `eject`.
 *
 * 15 fills the reserved `projectile` replication tier — the last one that was being held open.
 * `projectiles` on the `snapshot` and `delta` frames carries the room's rockets, mines and
 * grapple chains: the whole set, not an interest-culled one, on every frame rather than on a
 * divisor, because unlike an orb a projectile moves. Nothing else changed shape — a power-up's
 * ability is a `ProjectileSpec` in the registry both ends already read, so the three abilities
 * this version was bumped for add exactly one list to the wire between them.
 *
 * 16 puts the room's NAME on `welcome`. Every room has one now — an operator's for a declared
 * room, a seeded funny one (`roomNameFor`) for a room the matchmaker invented — and a player is
 * never shown a room id anywhere. One short string once per join; nothing per tick.
 *
 * 17 adds canned sayings. `saying` on the `input` frame is the fifth action to ride the movement
 * frame, after `duelChoice`, `split`, `eject` and `usePowerUp`, and it carries an *id*: a client
 * can never put words of its own on another player's screen, which is the whole security design
 * of the feature. `sayings` on the `snapshot` and `delta` frames carries the bubbles the other
 * way, as resolved text, because half of a player's vocabulary is their own approved submissions
 * and no other client has ever heard of those. See `SayingSnapshot` for both halves of that.
 *
 * 18 adds SPECTATING. `camX`/`camY` on the `input` frame are the camera centre a spectator is
 * looking at — the only positional value the server ever takes from a client, and it is safe
 * precisely because a spectator cannot act: it decides what is replicated to them and nothing
 * else, it is clamped to the room's walls, and it is ignored outright for a player. `spectator`
 * on `welcome` is the server saying which of the two this socket is, resolved from the signed
 * ticket rather than from anything the client asked for. Nothing else moved: a spectator is
 * replicated through the same interest selection, on the same frames, as everybody else.
 *
 * 19 adds one error code, `replaced_elsewhere`: this account started a game somewhere else and
 * this connection is the older of the two. No frame changed shape — the code is appended to
 * `PROTOCOL_ERROR_CODES`, which the binary codec writes as an index — but a client that does not
 * know the word cannot draw the screen it is for, so it is a version all the same.
 *
 * 20 adds `killerIsBot` to the `died` frame. The death screen names who ate you and marks them
 * as software or as a person, and the `bots` channel cannot answer that on its own: it names a
 * player on the frame they enter the viewer's interest set, so a killer the interest budget had
 * culled — or one who was never in view at all — would leave the client guessing at the very
 * moment it has to say something. The room knows, and it is one byte once per run.
 *
 * 21 adds the `roster` frame: every player in the room by name, with their bot flag and their
 * country, on a 1 Hz tier of its own. It is the room-wide channel the human/bot counters needed
 * to be able to open into a list of *who* — the client's own view is interest-culled, so a panel
 * built from it would name a fraction of the room and would change as the viewer moved. Its own
 * frame rather than a field on `leaderboard`, and `UPDATE_RATE_HZ.roster` explains why it is the
 * slowest tier on the wire; both are stated on `serverRosterSchema`.
 *
 * 22 adds `frozen` to the `snapshot` and `delta` frames: the visible players a freeze ray still
 * has. A frozen blob is one that does not answer its steering, so a viewer who was not told would
 * read the room as broken rather than as somebody having been frozen. It travels the way a duel
 * does — the whole visible set on every frame, no `frozenRemoved`, interest-scoped because it is
 * drawn *on a blob* — and it is a bare list of ids rather than a shape of its own, because unlike
 * a duel there is nothing on this client counting it down: the frame stops naming a player on the
 * tick they thaw. A quiet frame costs one zero-length varint.
 *
 * 23 adds `slowed` to the `snapshot` and `delta` frames beside `frozen`, and a `frost` power-up
 * for it to be about. It is `frozen`'s field in every respect — the whole visible set on every
 * frame, no `slowedRemoved`, interest-scoped because it is drawn *on a blob*, a bare list of ids
 * because nothing on the client counts it down — and it is a *separate* list rather than a flag
 * folded into that one because the two states compose: a player can be frozen and slowed at once,
 * they end at different ticks, and they are drawn differently. A quiet frame costs one more
 * zero-length varint.
 *
 * 24 adds the `notice` frame: the room's event feed — somebody collected a power-up, somebody
 * used one on somebody, somebody was shattered, somebody was eaten. It is its own frame for the
 * `roster` frame's reasons and it is room-wide for the `roster` frame's reason: an orb taken by
 * a player the viewer cannot see is still worth being told about, and an interest-scoped channel
 * cannot say so. Where it goes further than the roster is that it is sent on **no** schedule at
 * all — there is a frame on the ticks something happened and nothing whatever on the others, so a
 * quiet room pays not one byte for it. `serverNoticeSchema` argues all of that; `GameNotice` is
 * where the names being resolved server-side is argued.
 *
 * 25 replaces `heldPowerUp` on the `snapshot` and `delta` frames with `inventory`, and adds
 * `selectSlot` to the `input` frame. A player carries several distinct power-ups now
 * (POWER_UP_INVENTORY_SIZE) with one of them selected, and each is worth either a window of time
 * or a number of charges — so the one field that used to be a nullable id is a list of slots plus
 * the index of the selected one, and there is a sixth action on the movement frame to move that
 * index. **`selectSlot` names a SLOT and never a kind**, which is `usePowerUp` naming nothing at
 * all, one step along: a client that could name the power-up to select could select one it never
 * picked up. An empty inventory is still one zero byte, because the codec writes the index only
 * when there is a slot for it to point at.
 *
 * 26 adds `invisible` to the `snapshot` and `delta` frames beside `frozen` and `slowed`, and an
 * `invisibility` power-up for it to be about. It is those two fields in every respect — the whole
 * visible set on every frame, no `invisibleRemoved`, a bare list of ids because nothing on the
 * client counts it down — and it is a third list rather than a flag on either, because the three
 * statuses compose and end at different ticks.
 *
 * **WHAT IT IS ACTUALLY FOR IS DIFFERENT FROM BOTH OF THEM, and that is worth stating on the
 * wire's own card.** A hidden player is not replicated to anybody else *at all*
 * (`selectInterest`), so the only id this list can ever carry is the viewer's own: it is how a
 * ghost is told they are a ghost, not how a room is told where one is. Being interest-scoped is
 * therefore not a saving here, it is the mechanism — there is no frame anywhere that names a
 * hidden player to somebody who is not them.
 *
 * 27 adds `spawnProtected` to the `snapshot` and `delta` frames beside `frozen`, `slowed` and
 * `invisible`: the visible players who joined less than SPAWN_PROTECTION_SECONDS ago and cannot be
 * touched yet. It is those three fields in every respect — the whole visible set on every frame,
 * no `spawnProtectedRemoved`, a bare list of ids because nothing on the client counts it down —
 * and a fourth list rather than a flag on any of them, because the four statuses compose and end
 * at different ticks.
 *
 * **IT IS `invisible`'s IMMUNITY AND THE OPPOSITE OF ITS REPLICATION, which is the one thing to
 * read twice here.** A hidden player is on nobody else's frame at all, so that list can only ever
 * name the viewer; this one names whoever the viewer can see, and being seen is the entire point
 * — a throb the room can read is what makes a player it is not worth driving at. `selectInterest`
 * therefore knows nothing about it.
 *
 * 28 adds `running` to `InventorySlotSnapshot`: whether a timed slot's window is actually
 * counting down. It is there because a window now starts at its FIRST FIRING rather than at the
 * pickup — the player's own correction, "the rockets should NOT start until the user fires the
 * first rocket" — so an unstarted slot exists, and neither of the two client-side rules that turn
 * on it can be derived from `ticksLeft`: an unstarted window deliberately reports its whole
 * duration there so a chip reads "10s" before it is spent, which makes it identical by value to a
 * window that has only just begun. One byte per carried slot on the viewer's own frame.
 *
 * 29 adds `stake` to `DuelSnapshot`: the mass the winner of this duel takes, or null for a duel
 * two players walked into. The duel gun is why there is such a thing — a duel fired *across* a
 * mismatch cannot stake a share of the loser without the same button being worth wildly different
 * amounts in the two directions, so a gun duel fixes an absolute number when it begins. The new
 * registry id itself is NOT what bumps this: an orb and a projectile have always carried their
 * kind as a string, so a ninth power-up is a value on a field that already exists. What bumps it
 * is one nullable number on a frame both duellists read for ten seconds.
 *
 * 30 adds `prizeShards` to the `snapshot` and `delta` frames: the pieces of a busted pot lying on
 * the floor. It is `powerUps`' field in every respect and was written by copying it — room-wide
 * rather than interest-scoped, because a shard is map furniture and a *destination* and being
 * able to see one across the arena on the minimap is most of what makes crossing the arena for it
 * worth doing; whole rather than diffed, because there are a handful and a diff that drifted
 * would leave one painted on a minimap that nothing could clear; and **nullable on the delta**,
 * because shards do not move, so the list rides the same 5 Hz `powerup` divisor the orbs do and
 * `null` is "this frame says nothing about shards" rather than "there are none".
 *
 * **WHAT A SHARD IS WORTH IS NOT ON THIS FRAME AND MUST NEVER BE.** A `PrizeShardSnapshot` is an
 * id and a place, exactly as a spike's is; the mass is applied server-side on pickup. That is
 * both the mechanic — a player cannot pick the fat one out of five — and the reason this costs
 * what a spike costs: PELLET_WIRE_MASS_MAX is 12, so a shard worth hundreds could not have
 * ridden the pellet channel at all.
 *
 * 31 adds `pot` to `DuelSnapshot`: how high a push-your-luck mini-game's stake has climbed, 0..100,
 * or null for a game that has no pot — which is every game the registry can hand out today, so
 * every frame this build actually sends carries the same single byte saying "none". A duel is now
 * able to be a game about *when* rather than about *what*, and a client cannot draw a climbing pot
 * it is not being told the height of.
 *
 * **WHAT IS NOT ON THE FRAME IS THE POINT OF THE FRAME.** The tick the pot bursts on is drawn per
 * duel from the room's seeded stream and stays in the process: there is no field for it here, no
 * codec writes one, and `toDuelSnapshot` has nowhere to put it. That is the choice-secrecy rule
 * again and a harder case than it — a client that knew the bust point would grab one tick before
 * it, every time — and it is contained the same way, by there being nothing to leak rather than by
 * a rule somebody has to remember. Who has grabbed needs no new field either: a pot game declares
 * `secretUntilLocked: false`, so `aLocked` / `bLocked` and the per-side choice already say it on
 * the tick it happens, which is exactly what makes the game watchable.
 *
 * 32 adds the `vote` frame and `voteAnswer` on the `input` frame: the room-wide vote. The frame is
 * its own kind rather than a field anywhere, for the `roster` frame's three reasons and the
 * `notice` frame's fourth — it is room-wide so it is built once and encoded once, it carries no
 * position, and it is on **no tier at all**, so a room where nobody has called a vote pays not one
 * byte for the channel. See `serverVoteSchema` for the one per-viewer field on it and what that
 * exception costs.
 *
 * **AN OPEN VOTE CARRIES NO TALLY, NO ANSWER AND NO LIST OF WHO HAS ANSWERED.** That is
 * `toDuelSnapshot`'s secrecy rule applied to a room instead of to a pair: a running count is a
 * running instruction to join whichever side is winning, so the ten seconds would decide
 * themselves in the first two. The question, its answers and its deadline are the whole of what an
 * open vote says, and `binary-codec.test.ts` pins that against a real encoded frame rather than by
 * inspection.
 *
 * 33 adds the star, in three places and not one.
 *
 * `stars` on the `snapshot` and `delta` frames is `prizeShards`' field copied field for field:
 * room-wide rather than interest-scoped because a star is map furniture and a *destination* and
 * has to be on the minimap for the orb's and the shard's reason; an id and a place and nothing
 * else, because STAR_RADIUS is a shared constant and there is one kind of star; whole rather than
 * diffed; and **nullable on the delta**, because a star does not move, so it rides the same 5 Hz
 * `powerup` divisor and `null` is "this frame says nothing about stars" rather than "there is
 * none". A separate list rather than a widening of `prizeShards` because the two are different
 * entities with different radii, different rules about who may take one and — the one that decides
 * it — completely different consequences for taking one.
 *
 * **THE TAKER'S CHOICE IS AN EXTENSION OF `voteAnswer` AND NOT A NEW EDGE**, which is the decision
 * this version turns on. A star opens an ordinary room-wide vote, so its taker already has a field
 * on the input frame for saying HEADS or TAILS and a rule (`RoomSim.recordVoteAnswer`) that
 * validates it against that vote's own ballot, refuses a second answer, and refuses one after the
 * deadline. The gamble is that field; **the auto-win is the absence of one**. A new edge would have
 * been a second way to say the same word, needing its own validation against the same ballot and
 * its own first-wins rule, in order to express a choice the existing field already expresses — and
 * "skip the guess" is not a thing to send, it is a guess not sent. What the simulation does with
 * the taker's answer is different (it goes on the star's claim rather than into the tally, so a
 * gambler cannot add a vote to the side they bet on), but that is a routing decision inside
 * `RoomSim` and not a shape on the wire.
 *
 * `star` on the `vote` frame is the one thing that had to be added for it: a per-viewer boolean
 * saying "you are this vote's star-taker", on the OPEN frame as well as the settled one, because
 * the choice has to be made before the result is known. `VoteResultSnapshot.starOutcome` is its
 * room-wide half — what the taker did and how it went — which is what lets the settled card say so
 * without a second notice repeating a frame everybody already has.
 *
 * **WHAT A STAR IS WORTH IS NOWHERE ON THE WIRE**, and there is nothing to put there: the payout is
 * three times the taker's own mass out of a pot that does not exist until the vote settles, so the
 * only number involved is the one `self.massChange` reports afterwards.
 *
 * 34 adds the sprint, in two places and not one.
 *
 * **`sprint` ON THE `input` FRAME IS THE FIRST HELD LEVEL ON THAT FRAME THAT ANYTHING READS, AND
 * IT BREAKS THE RULE EVERY OTHER ACTION ON IT FOLLOWS.** `split`, `eject`, `usePowerUp`, `saying`
 * and `selectSlot` are all *edges*: one frame carrying one is one action, and a client must not
 * re-send one until it is acknowledged. This one is a *state*, exactly as `dirX`/`dirY` are — it
 * is sent on every frame the control is down and simply absent from the frames after it comes up,
 * so a frame that does not carry it is a frame that says "not sprinting". That is the safe
 * failure and it is why the field is shaped this way: a lost frame ENDS a sprint rather than
 * extending one, where an edge that went missing would cost an action and an edge that arrived
 * twice would buy two.
 *
 * **THE METER IS THE VIEWER'S OWN AND NOBODY ELSE'S**, which is `inventory`'s rule rather than
 * `frozen`'s: `stamina` is one varint on the viewer's own frame, every frame, and there is
 * deliberately no per-player stamina on `PlayerSnapshot` — that would be a byte per player per
 * tick on the tier that already caps players-per-core, to say something this game does not tell
 * you about anybody else. It is carried as a whole percentage point (0..STAMINA_MAX, which *is*
 * 100) rather than as the float the simulation holds, on `DuelSnapshot.pot`'s argument: the bar
 * is a readout, a point of it is 40 ms of sprint, and a client that had to divide would be a
 * client that could disagree with the server about what full looks like.
 *
 * **BUT A SPRINTING PLAYER IS VISIBLE, AND THAT IS A DELIBERATE COST RATHER THAN A SAVING.**
 * `sprinting` is a fifth interest-scoped id list beside `frozen`, `slowed`, `invisible` and
 * `spawnProtected`, in their exact shape — the whole visible set every frame, no removal list,
 * bare ids. What it buys is that a blob moving at half again the speed its mass says has
 * something on it saying why; the alternative reads as a bug or as a cheat, which is the one
 * thing a speed change must never look like. What it costs is a zero-length varint on every frame
 * of every viewer who can see nobody sprinting, and a two-byte id per visible sprinter — the same
 * bill the four lists beside it already pay, and the reason it is a fifth list rather than a flag
 * folded into one of them is theirs: all five compose, end at different moments and are drawn
 * differently.
 *
 * 35 adds `outcomeReason` to `DuelSnapshot`: WHY a duel ended the way it did, or null for a game
 * with only one way to end. One byte per duel on a channel that already carries a phase index and
 * an outcome index, and it is a real gap rather than a nicety — a push-your-luck game has two
 * different losses that read identically as an outcome and mean opposite things ("they grabbed
 * first" against "you grabbed before the pot armed and paid them for it"), so without it both
 * arrive as the same unexplained loss. That is the bug this bump exists to close.
 *
 * **AN UNKNOWN REASON DEGRADES TO A PLAIN WIN OR LOSS, AND THAT IS TESTED RATHER THAN ASSUMED.**
 * The set is indexed on the wire, `NOTICE_KINDS`' arrangement, so a client one build behind a
 * server that has added a fourth reason reads an index it has never heard of. It decodes to null
 * and the overlay renders exactly what it rendered before this field existed. A bundled APK has
 * no OTA route, so degrading is the only acceptable answer — see `readDuelOutcomeReason`.
 *
 * **AND IT SAYS NOTHING ABOUT THE HIDDEN POINTS BEHIND IT.** `early_grab` tells a player the grab
 * was early and never how early; the tick a pot arms on is drawn per duel, is on no frame and is
 * in neither codec, which is `DuelSnapshot.pot`'s own rule about the bust point one field along.
 *
 * 36 adds the deathmatch room, in two places and not one.
 *
 * The `match` frame is what tells a client which round it is in: the phase, the deadline that
 * phase ends on, the round number, how many are waiting, how many the round is waiting for, how
 * many are still standing, and — once there is one — who won. It is its own frame for the
 * `roster` frame's three reasons and the `notice` frame's fourth, and it is the `vote` frame's
 * arrangement throughout: room-wide so it is built once and encoded once, carrying no position,
 * and **on no tier at all**, so a classic room pays not one byte for the channel and a deathmatch
 * room pays only on the ticks the thing it describes actually moved. See `serverMatchSchema` for
 * the one per-viewer field on it and what that exception costs.
 *
 * **`RoomInfo.mode` ON `welcome` IS THE OTHER HALF, AND IT IS NOT REDUNDANT WITH THE FRAME.** A
 * client has to know what kind of room it has joined *before* any match frame arrives — a
 * deathmatch room's first frame is the one that opens its lobby, and a player who joined
 * mid-round waits for the next one, which may be half a minute in which a client with no mode
 * has nothing to say. It rides `welcome` once for `RoomInfo.name`'s reason: a room's mode is
 * fixed for its life, and `liveRoomSettingsPatchSchema` refuses to move it out loud.
 *
 * **NEITHER THE PHASE NOR THE MODE MAY FAIL A FRAME, and both degrade instead** — the rule
 * `readDuelOutcomeReason` is under and for its stated reason: a bundled APK has no OTA route, so
 * a client one build behind a server that has appended a phase or a mode has to keep playing. An
 * unknown phase reads as `waiting` and an unknown mode reads as absent, which every reader
 * already takes as `classic`; both are argued where they are decoded.
 *
 * 40 adds ENDING A BOT-ONLY ROUND, in two places.
 *
 * **`endRound` on the `input` frame is the seventh action to ride it**, after `duelChoice`,
 * `split`, `eject`, `usePowerUp`, `saying`, `selectSlot` and `voteAnswer` — and it is an EDGE
 * like the first six rather than a level like `sprint`: one frame carrying it is one request, and
 * a client must not re-send it until the match frame says the round has ended. It names nothing
 * at all, `usePowerUp`'s rule: which round is being ended is the room's own answer, and a client
 * that could name one could end a round it is not in.
 *
 * **`humansAlive` on the `match` frame is what the button is drawn off**, and it is a second
 * count beside `alive` rather than a narrowing of it: `alive` is bodies and is what the HUD says
 * "7 alive" from, and this is the half of that number the room is actually being played for. A
 * client cannot derive it — the `bots` channel is interest-scoped, so it names only the players a
 * viewer can see, which is exactly the wrong set for a question about the whole room, and the
 * roster frame says who is in the room rather than who is still standing. **It is never larger
 * than `alive`**, and zero during every phase but `live` for `alive`'s own reason.
 *
 * The refusal is the server's either way (`RoomSim.requestEndRound`), so the count decides what a
 * player is OFFERED and never what they are allowed.
 *
 * 41 puts SPIKES ON THE DELTA, because a spike stopped being a fixture.
 *
 * It is consumed by the cell it breaks, comes back somewhere else SPIKE_RESPAWN_TICKS later, and
 * can be launched across the map by being fed SPIKE_FEED_COUNT ejected pellets — so the snapshot's
 * "only ever here, a spike is a fixture" is no longer true and the delta has to be able to say
 * what changed. `serverDeltaSchema.spikes` is nullable and on NO tier: null on every frame nothing
 * happened to the field, which is nearly all of them, and the whole set on the frames one is
 * broken, respawns or moves. One byte on a quiet frame.
 *
 * 42 adds a TEAM ROUND'S SCOREBOARD to the `match` frame.
 *
 * `teams` is both sides' total mass and bodies left, or null in every room that is not a `teams`
 * room — so a classic room still sends no match frame at all and a deathmatch room's is byte for
 * byte what it was. Neither number is derivable on this side: every player list on the wire is
 * interest-scoped, so a viewer can only ever see part of a side.
 *
 * `yourTeam` is the per-viewer half, `youWon`'s arrangement and its price. The blob's colour is
 * what says which side somebody is on in the arena — a team's hue REPLACES the player's own, so
 * `PlayerSnapshot` needed nothing and the 20 Hz tier is untouched.
 *
 * 43 puts `fed` on a SPIKE, so a spike that is swallowing ejections can show it.
 *
 * One small varint on a list that is already only sent when the field changes — and feeding one
 * IS a change, so it costs nothing a feed was not already paying for. The size itself stays off
 * the wire: `spikeRadiusFor(fed)` is shared, so the renderer sizes the star, the outline dents
 * against it and a pellet has to reach it, all from one definition.
 *
 * 44 puts MERGED PAIRS on the wire, which is what makes the team merge something a player can
 * see and use rather than a rule the simulation runs on its own.
 *
 * `merges` is interest-scoped beside `sayings` and for its reason (it is drawn on a blob), whole
 * on every frame beside `duels` and for theirs (it is a state with an end, so the frame that stops
 * mentioning one is how a client learns it came apart), and a single zero byte in every room that
 * is not a `teams` room.
 *
 * It carries both the names and the ids: the names go on the blob, and the ids are how a client
 * works out whether one of them is *itself* — which decides whether it is told it is steering or
 * that it has the buttons. A name is not an identity, which is the rule `NoticeOverlay` refuses to
 * have a `self` flag under.
 */
export const PROTOCOL_VERSION = 44;

/**
 * `t` is the discriminator on every message. One character because it is repeated on every frame
 * at 20 Hz, and a fixed key so a binary codec can map it to a single byte later.
 */

const entityIdSchema = z.string().min(1).max(64);
const tickSchema = z.number().int().nonnegative();
const massSchema = z.number().nonnegative();
const hueSchema = z.number().int().min(0).max(359);
/** Epoch milliseconds. Clamped to int so a client cannot smuggle NaN or Infinity into RTT math. */
const timestampSchema = z.number().int().nonnegative();

// ---------------------------------------------------------------------------
// Shared payload shapes
// ---------------------------------------------------------------------------

/**
 * One cell of a cluster.
 *
 * `x`, `y` and `mass` are whole units by the time they get here — every producer rounds them
 * through `quantisePosition` / `quantiseMass` (`constants/wire.ts`), which is where the choice
 * of precision and the reasoning behind it live. The schema itself still accepts a float: it is
 * also what a *client* parses server frames with, and a cosmetic digit is not worth refusing a
 * frame over. A binary codec replacing this encoding inherits the same precision.
 */
export const cellSnapshotSchema = z.object({
  id: entityIdSchema,
  x: z.number(),
  y: z.number(),
  mass: massSchema,
}) satisfies z.ZodType<CellSnapshot>;

/**
 * A player is its cluster: no player-level x, y or mass, because all three are exact functions
 * of `cells` (see `PlayerSnapshot`). The list is bounded by the same MAX_CELLS_PER_PLAYER the
 * simulation enforces, so a forged frame cannot make a client draw an unbounded cluster.
 *
 * The player's chosen photo is deliberately NOT here — it is `playerSkinSchema`, sent once when
 * a player enters a viewer's interest set rather than on every frame. See `PlayerSkin`.
 */
export const playerSnapshotSchema = z.object({
  id: entityIdSchema,
  name: z.string().max(64),
  hue: hueSchema,
  cells: z.array(cellSnapshotSchema).min(1).max(MAX_CELLS_PER_PLAYER),
}) satisfies z.ZodType<PlayerSnapshot>;

/**
 * Longest skin URL the wire will carry. An R2 public base plus `skins/<uuid>/<uuid>.<ext>` is
 * comfortably inside 200 characters, so this is headroom for a custom domain rather than a
 * budget anything runs up against — and a ceiling, so a row that somehow held a megabyte of
 * "URL" could not be replicated to every viewer in the room.
 */
export const MAX_SKIN_URL_LENGTH = 512;

/**
 * The image one player is playing as. Absent for a player without one — see `PlayerSkin` for
 * why this is its own list rather than a field of `playerSnapshotSchema`.
 *
 * The scheme is checked rather than assumed. This value is minted by the API server and rides a
 * signed ticket, so it is not attacker-controlled in a correctly configured deployment; the
 * check is here because the *client* parses server frames with this schema too, and "the
 * renderer will only ever be handed an http(s) URL to load" is worth being a property of the
 * protocol rather than of every call site downstream of it.
 */
export const playerSkinSchema = z.object({
  id: entityIdSchema,
  url: z
    .string()
    .max(MAX_SKIN_URL_LENGTH)
    .url()
    .refine((value) => value.startsWith('http://') || value.startsWith('https://'), {
      message: 'Skin URL must be http(s)',
    }),
}) satisfies z.ZodType<PlayerSkin>;

/**
 * ISO 3166-1 alpha-2, upper case, and nothing else.
 *
 * Enumerating the ~250 live codes here was rejected: the list changes (rarely, but it does), a
 * client too old to know a new one would refuse the whole frame rather than draw a blank chip,
 * and nothing downstream of this cares which country it is — the renderer maps the two letters
 * to a flag and shows the letters when it cannot. So the shape is checked and the membership is
 * not. Anything that is not two ASCII capitals never reaches the wire in the first place;
 * `resolveClientCountry` normalises before this is ever asked.
 */
export const countryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Country must be ISO 3166-1 alpha-2');

/**
 * Where one player is playing from. Absent for a player the server could not place, and for
 * every bot — see `PlayerCountry` for why this is its own list rather than a field of
 * `playerSnapshotSchema`.
 */
export const playerCountrySchema = z.object({
  id: entityIdSchema,
  country: countryCodeSchema,
}) satisfies z.ZodType<PlayerCountry>;

/**
 * A pellet. `size` is its quantised mass — see `PelletSnapshot` for why it is a step rather than
 * a mass, and `PELLET_SIZE_STEPS` for how wide a step is. Bounded here so a forged frame cannot
 * make a client draw a dot the size of the map.
 */
export const pelletSnapshotSchema = z.object({
  id: entityIdSchema,
  x: z.number(),
  y: z.number(),
  hue: hueSchema,
  size: z.number().int().min(0).max(PELLET_SIZE_STEPS),
}) satisfies z.ZodType<PelletSnapshot>;

/**
 * A power-up id, bounded but not enumerated.
 *
 * The wire deliberately does not know one power-up from another: which ids exist is a property
 * of the registry in `@chompdome/shared/powerups`, and both ends resolve the id through it. So
 * this checks only what a schema can check without the registry — that the value is short enough
 * to be an id at all — exactly as `miniGameChoiceSchema` does. Enumerating them here would mean
 * bumping the protocol every time a power-up is added, which is the one thing the registry
 * exists to avoid.
 */
export const powerUpIdSchema = z.string().min(1).max(MAX_POWER_UP_ID_LENGTH);

/**
 * The power-ups a player is carrying INTO a match, sealed into their signed WebSocket ticket.
 *
 * It lives beside `powerUpIdSchema` rather than with the other ticket claims for
 * `countryCodeSchema`'s stated reason: what comes out of a ticket here goes straight into an
 * inventory slot, and a slot's `kind` is on the wire as `InventorySlotSnapshot.kind` — so "what a
 * power-up id is" has to be one definition rather than two that can drift.
 *
 * **The list is DISTINCT KINDS and never a count**, which is the inventory's own rule read one
 * process earlier: a slot holds a kind, and what that slot is worth — a window or a budget of
 * charges — is the registry's answer rather than anything a wallet or a ticket gets to say. So
 * the bound is POWER_UP_INVENTORY_SIZE, because that is how many slots there are to fill.
 *
 * Nothing here checks that an id is one this build **registers**, exactly as `sayingIdSchema`
 * does not check that a saying exists: enumerating the registry in a schema would mean bumping
 * the protocol every time a power-up was added. `RoomSim.takePowerUp` is where an unregistered
 * id is dropped, which is the one place that rule already lived.
 */
export const ticketCarriedPowerUpsSchema = z.array(powerUpIdSchema).max(POWER_UP_INVENTORY_SIZE);

/**
 * One slot of the viewer's own inventory — see `InventorySlotSnapshot` for what the two counters
 * mean and for why the firing cooldown is not among them.
 *
 * The bounds are the registry's rather than the wire's: `ticksLeft` cannot exceed the longest
 * window any entry declares and `charges` cannot exceed the largest budget, but neither of those
 * is knowable from this file without importing the whole registry, and a schema that guessed at
 * them would refuse a frame the day somebody tuned a number. Non-negative integers is the whole
 * of what the wire can honestly say; the simulation is what decides they are reachable values.
 */
export const inventorySlotSchema = z.object({
  kind: powerUpIdSchema,
  ticksLeft: z.number().int().min(0),
  charges: z.number().int().min(0),
  /** Whether a timed slot's window is counting down; see `InventorySlotSnapshot.running`. */
  running: z.boolean(),
});

/**
 * What THIS viewer is carrying, and the one per-viewer field on a world frame besides `ackSeq`.
 *
 * Bounded at POWER_UP_INVENTORY_SIZE here rather than left open, because unlike the two counters
 * above it that is a number this side genuinely knows: the simulation will never send a fourth
 * slot, so a frame carrying one is a frame from a peer that disagrees about the protocol and the
 * `PROTOCOL_VERSION` handshake is what that is for.
 *
 * `selected` is only *meaningful* against `slots`, and the schema deliberately does not try to
 * relate them — an empty inventory carries 0, and the codec does not put the byte on the wire at
 * all. A reader that finds `selected` past the end of `slots` draws nothing selected, which is
 * the same thing an empty inventory draws.
 */
export const playerInventorySchema = z.object({
  slots: z.array(inventorySlotSchema).max(POWER_UP_INVENTORY_SIZE),
  selected: z.number().int().min(0),
});

/**
 * One orb on the field. No radius and no expiry — see `PowerUpSnapshot` for both.
 */
export const powerUpSnapshotSchema = z.object({
  id: entityIdSchema,
  kind: powerUpIdSchema,
  x: z.number(),
  y: z.number(),
}) satisfies z.ZodType<PowerUpSnapshot>;

/**
 * One projectile. `kind` is the same registry id an orb carries — the wire says which power-up
 * fired this, and what that power-up *does* stays a lookup on both sides. No radius and no
 * velocity; see `ProjectileSnapshot` for both.
 */
export const projectileSnapshotSchema = z.object({
  id: entityIdSchema,
  kind: powerUpIdSchema,
  ownerId: entityIdSchema,
  x: z.number(),
  y: z.number(),
  armed: z.boolean(),
}) satisfies z.ZodType<ProjectileSnapshot>;

/**
 * One player's speech bubble — see `SayingSnapshot`.
 *
 * `text` is bounded, which is the only check a schema can make about it: a client parses server
 * frames with this schema too, and "the renderer will never be handed a megabyte to draw over a
 * blob" is worth being a property of the protocol rather than of the one call site upstream that
 * happens to enforce it today.
 */
export const sayingSnapshotSchema = z.object({
  id: entityIdSchema,
  text: z.string().min(1).max(SAYING_TEXT_MAX_LENGTH),
  endsAtTick: tickSchema,
}) satisfies z.ZodType<SayingSnapshot>;

/**
 * A merged pair: whose body it is, who is inside it, and when it comes apart. See `MergeSnapshot`.
 */
export const mergeSnapshotSchema = z.object({
  id: entityIdSchema,
  controllerId: entityIdSchema,
  memberIds: z.array(entityIdSchema).min(1).max(TEAM_MERGE_MAX_MEMBERS),
  // Bounded by the same 64 a player frame and a roster row bound a name at, so this schema is
  // never the thing that refuses a frame a `PlayerSnapshot` would have carried happily.
  names: z.array(z.string().max(64)).min(1).max(TEAM_MERGE_MAX_MEMBERS),
  endsAtTick: tickSchema,
}) satisfies z.ZodType<MergeSnapshot>;

/**
 * A spike ball: where it is, and **how many ejections it has swallowed**.
 *
 * No hue and still no radius — a spike's size is `spikeRadiusFor(fed)`, which every build already
 * has, so what travels is the one number a client cannot work out for itself. That is the same
 * bargain `PelletSnapshot.size` strikes and the one `PowerUpSnapshot` strikes with its kind: the
 * wire carries the state and the shared constants carry the shape.
 *
 * `fed` is 0 for every spike in a room nobody has fed and is strictly under SPIKE_FEED_COUNT,
 * because reaching it is what makes a spike burst and go back to zero.
 */
export const spikeSnapshotSchema = z.object({
  id: entityIdSchema,
  x: z.number(),
  y: z.number(),
  fed: z.number().int().nonnegative().max(SPIKE_FEED_COUNT),
}) satisfies z.ZodType<SpikeSnapshot>;

/**
 * A prize shard. No radius, no expiry and — the one that matters — **no mass**: what a shard is
 * worth is applied server-side on pickup and is never on the wire. See `PrizeShardSnapshot`.
 */
export const prizeShardSnapshotSchema = z.object({
  id: entityIdSchema,
  x: z.number(),
  y: z.number(),
}) satisfies z.ZodType<PrizeShardSnapshot>;

/**
 * A star. The shard's shape exactly — an id and a place — and for its reasons: STAR_RADIUS is a
 * shared constant, there is one kind of star, and how long it has left is nobody's business. See
 * `StarSnapshot`.
 */
export const starSnapshotSchema = z.object({
  id: entityIdSchema,
  x: z.number(),
  y: z.number(),
}) satisfies z.ZodType<StarSnapshot>;

/**
 * One answer a vote offers — see `VoteOption` for why the id is what travels back.
 *
 * The label is bounded rather than enumerated, exactly as a saying's resolved text is: a client
 * parses server frames with this schema too, and "the renderer will never be handed a megabyte to
 * draw on a ballot" is worth being a property of the protocol.
 */
export const voteOptionSchema = z.object({
  id: z.string().min(1).max(MAX_VOTE_OPTION_ID_LENGTH),
  label: z.string().min(1).max(MAX_VOTE_OPTION_LABEL_LENGTH),
}) satisfies z.ZodType<VoteOption>;

/** How many people answered one option, once the vote is over — see `VoteTally`. */
export const voteTallySchema = z.object({
  optionId: z.string().min(1).max(MAX_VOTE_OPTION_ID_LENGTH),
  votes: z.number().int().nonnegative(),
}) satisfies z.ZodType<VoteTally>;

/**
 * What a resolved vote decided. Room-wide and identical for everybody — see `VoteResultSnapshot`.
 *
 * `winner` is nullable because "nothing won" is a real outcome and the commonest one: nobody
 * answered, or two options tied. A tie is deliberately not broken — see `VoteResultSnapshot`.
 */
export const voteResultSchema = z.object({
  winner: z.string().min(1).max(MAX_VOTE_OPTION_ID_LENGTH).nullable(),
  tallies: z.array(voteTallySchema).max(MAX_VOTE_OPTIONS),
  massMoved: massSchema,
  /**
   * What the star-taker did with this vote, or null when no star opened it — which is every vote
   * any other caller ever opens. Room-wide rather than per-viewer, unlike `self`: the whole room
   * watched the star go up, so what became of the player who took it is the room's business.
   */
  starOutcome: z.enum(['auto', 'won', 'lost']).nullable() satisfies z.ZodType<StarOutcome | null>,
}) satisfies z.ZodType<VoteResultSnapshot>;

/**
 * What THIS viewer got out of a resolved vote — the one per-viewer field on the vote frame.
 *
 * `massChange` is signed, so it is a plain number rather than a `massSchema`: a loser's is
 * negative, and that is the whole reason the field exists.
 */
export const voteSelfSchema = z.object({
  choice: z.string().min(1).max(MAX_VOTE_OPTION_ID_LENGTH),
  massChange: z.number(),
}) satisfies z.ZodType<VoteSelfSnapshot>;

/**
 * A mini-game choice id, bounded but not enumerated.
 *
 * The wire deliberately does not know rock from paper: which choices are legal depends on which
 * mini-game the duel is running, and that is a registry lookup the *simulation* does. So this
 * checks only what a schema can check without the world — that the value is short enough to be
 * a choice id at all — and `RoomSim` rejects anything that is not a choice of that duel's game.
 * Enumerating them here would mean bumping the protocol every time a mini-game is added.
 */
const miniGameChoiceSchema = z.string().min(1).max(MAX_MINI_GAME_CHOICE_LENGTH);

/**
 * A duel in flight. See `DuelSnapshot` for why the choices are nullable — that is the secrecy
 * rule, and it is enforced where the snapshot is built, not here.
 */
export const duelSnapshotSchema = z.object({
  id: entityIdSchema,
  gameId: z.string().min(1).max(32),
  phase: z.enum(['collecting', 'revealed']),
  aId: entityIdSchema,
  bId: entityIdSchema,
  aLocked: z.boolean(),
  bLocked: z.boolean(),
  aChoice: miniGameChoiceSchema.nullable(),
  bChoice: miniGameChoiceSchema.nullable(),
  outcome: z.enum(['a', 'b', 'draw']).nullable(),
  // Why it ended that way, or null for a game with only one way to end — which is every game
  // that simply compares two picks. Null is also what an unknown reason decodes to, so a client
  // one build behind a server never fails a whole frame over a value it has not heard of; see
  // `DuelOutcomeReason` and `readDuelOutcomeReason`.
  // Enumerated here exactly as `phase` and `outcome` above it are, and **the degrade for an
  // unknown reason lives one layer down rather than here**: the wire is binary, the set is
  // indexed, and `readDuelOutcomeReason` maps an index this build has never heard of back to null
  // before a schema ever sees it. That is what lets a client one build behind a server keep
  // playing and merely lose the explanation — see `DuelOutcomeReason`. The readable encoding is a
  // debugging aid switched on by hand (`PROTOCOL_CODEC=json`) and is held to the strictness the
  // two enums beside this one are.
  outcomeReason: z.enum(['grabbed_first', 'early_grab', 'burst']).nullable(),
  endsAtTick: tickSchema,
  // Null is a contact duel — see `DuelSnapshot.stake`. `massSchema` rather than a bare number so
  // a stake is bounded exactly as every other mass on this wire is.
  stake: massSchema.nullable(),
  // Null is a game with no pot. A whole percentage point and nothing else: the wire does not know
  // what a pot is worth and deliberately does not know when it bursts — see `DuelSnapshot.pot`.
  pot: z.number().int().min(0).max(MINI_GAME_POT_MAX_PERCENT).nullable(),
}) satisfies z.ZodType<DuelSnapshot>;

export const leaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  playerId: entityIdSchema,
  name: z.string().max(64),
  mass: massSchema,
  /** See `LeaderboardEntry.country` for why the flag is on this frame as well as on `countries`. */
  country: countryCodeSchema.optional(),
}) satisfies z.ZodType<LeaderboardEntry>;

export const leaderboardSelfSchema = z.object({
  rank: z.number().int().positive(),
  mass: massSchema,
}) satisfies z.ZodType<LeaderboardSelf>;

/**
 * One player as a notice names them — see `NoticeActor` for why there is no id here.
 *
 * `name` is bounded by the same 64 the player frame and the roster bound it at, so this schema
 * never becomes the thing that refuses a frame carrying a name those two would have carried.
 */
export const noticeActorSchema = z.object({
  name: z.string().max(64),
  bot: z.boolean(),
}) satisfies z.ZodType<NoticeActor>;

/**
 * One line of the room's event feed — see `GameNotice` and `NoticeKind`.
 *
 * The kind is enumerated here where a mini-game choice deliberately is not, and the difference is
 * who decides: which choices are legal depends on a registry the *simulation* reads, where a
 * notice kind is decided by this protocol and by nothing else.
 *
 * **A KIND IS A VERSION BUMP ON THE TICK SOMETHING CAN RAISE ONE, WHICH IS NOT ALWAYS THE TICK
 * THE WORD IS ADDED.** A client that does not know a word cannot draw the sentence, so a kind a
 * room can actually put on a frame is a bump; a kind nothing anywhere raises is a value in this
 * package and no byte on any wire — chicken's own shape, added to `@chompdome/shared` and left
 * out of `MINI_GAMES` so the client could learn to draw it before a server could hand one out.
 * `round_won` is in that state today: see `NoticeKind`, and see `collectNotices`, which has no
 * branch for the event that would produce one.
 */
export const gameNoticeSchema = z.object({
  kind: z.enum([
    'powerup_taken',
    'powerup_used',
    'shattered',
    'ate_player',
    'star_taken',
    'round_won',
    'said',
  ]),
  subject: noticeActorSchema,
  target: noticeActorSchema.nullable(),
  /** A registry id, never a name and never a glyph — the client looks the presentation up. */
  powerUp: powerUpIdSchema.optional(),
  /**
   * The words, for `said` alone — see `GameNotice.text`.
   *
   * Bounded by the same length a speech bubble's text is bounded by, which is the only check a
   * schema can make about it: a client parses server frames with this schema too, and "the feed
   * will never be handed a megabyte to draw" is worth being a property of the protocol rather
   * than of the one call site upstream that happens to enforce it today.
   */
  text: z.string().min(1).max(SAYING_TEXT_MAX_LENGTH).optional(),
}) satisfies z.ZodType<GameNotice>;

/**
 * One line of the room's roster — see `RosterEntry` for why the shape is this narrow.
 *
 * `name` is bounded by the same 64 the player frame bounds it at rather than by
 * DISPLAY_NAME_MAX_LENGTH, so this schema never becomes the thing that refuses a frame a
 * `PlayerSnapshot` would have carried happily.
 */
export const rosterEntrySchema = z.object({
  id: entityIdSchema,
  name: z.string().max(64),
  bot: z.boolean(),
  /**
   * Bounded by the largest room the simulation allows rather than by the room this frame came
   * from, which is what every coordinate on this protocol is bounded by: the schema does not know
   * the walls, and a forged value past them is clamped by the panel that draws it.
   */
  x: z.number().finite().min(0).max(MAX_WORLD_SIZE),
  y: z.number().finite().min(0).max(MAX_WORLD_SIZE),
  country: countryCodeSchema.optional(),
}) satisfies z.ZodType<RosterEntry>;

export const roomInfoSchema = z.object({
  id: entityIdSchema,
  /** Bounded by the same length the roster bounds an operator's typed name at. */
  name: z.string().min(1).max(ROOM_NAME_MAX_LENGTH),
  /**
   * What kind of match this room runs — see `RoomInfo.mode` for why it is here and why it is
   * optional. The enum is `ROOM_MODES`, so the modes a `welcome` may name and the modes an
   * operator may declare are one list rather than two.
   *
   * Deliberately **not** `.default('classic')`: a default would make a decoded frame stop being
   * deep-equal to the frame that was encoded, which is what every round-trip assertion in
   * `binary-codec.test.ts` is built on, and it would put the fallback in the schema where only
   * frames that go through it can see it. The fallback belongs at every reader, as `?? 'classic'`.
   */
  mode: z.enum(ROOM_MODES).optional(),
  worldWidth: z.number().positive(),
  worldHeight: z.number().positive(),
  tickRateHz: z.number().positive(),
  maxPlayers: z.number().int().positive(),
  playerCount: z.number().int().nonnegative(),
}) satisfies z.ZodType<RoomInfo>;

// ---------------------------------------------------------------------------
// client -> server
//
// Deliberately narrow. A client states *intent* — who it is, where it wants to go, what it
// picked — and nothing else. It never reports position, velocity, mass, what it ate, or who won
// a collision or a duel; all of that is the server's to decide at 20 Hz. Adding a field here is
// adding an attack surface, so new gameplay (powerups, missiles) belongs in new intent flags,
// never in positional data or in outcomes.
// ---------------------------------------------------------------------------

export const clientHelloSchema = z.object({
  t: z.literal('hello'),
  protocolVersion: z.number().int().nonnegative(),
  name: displayNameSchema,
  /** Matchmaker ticket, or absent for a guest joining an open room. */
  ticket: z.string().min(1).max(4096).optional(),
  roomId: entityIdSchema.optional(),
  /**
   * The shape of the client's viewport, width / height.
   *
   * The one thing about a viewer's screen the server cannot work out for itself, and it has to
   * know it: what has to be replicated is whatever the *camera frames*, which is a rectangle of
   * this shape (see `@chompdome/shared/constants` `viewHalfExtents`). Before this field the
   * server culled to a fixed circle and a wide window drew a third of its screen from stale
   * frames — entities near the edge flickering in and out as the player moved.
   *
   * It is not positional data and it is not trusted as more than a hint: it is clamped into
   * [MIN_VIEW_ASPECT, MAX_VIEW_ASPECT] and a missing or unusable value falls back to
   * DEFAULT_VIEW_ASPECT. Nor is it worth lying about — the view it shapes is held to a fixed
   * *area*, so a client claiming to be wider than it is trades height for width and is
   * replicated exactly as many entities either way.
   */
  aspect: z.number().positive().finite().optional(),
});

export const clientInputSchema = z.object({
  t: z.literal('input'),
  /** Per-connection monotonic counter; the server echoes the last seq it applied. */
  seq: z.number().int().nonnegative(),
  /**
   * Desired heading. The server normalises this and derives speed from mass, so an oversized
   * vector buys nothing — the bounds only exist to reject NaN/Infinity cheaply.
   */
  dirX: z.number().min(-1).max(1),
  dirY: z.number().min(-1).max(1),
  /** Intent flag, not an effect: the server decides whether the boost is affordable. */
  boost: z.boolean().optional(),
  /**
   * A mini-game duel answer, riding the movement frame rather than a message of its own.
   *
   * This is the whole of the duel's client -> server surface: one optional string on a channel
   * that is already parsed, already rate-limited and already capped at MAX_INPUTS_PER_TICK. A
   * dedicated `duel_choice` message would have been a second inbound frame kind to budget, size
   * and validate for one field. The client re-sends the same value on every input frame until
   * the duel snapshot says its side is locked, so a frame lost to the input cap costs a tick and
   * not the duel.
   */
  duelChoice: miniGameChoiceSchema.optional(),
  /**
   * "Split me now" — halve every cell of my cluster and throw the halves along my heading.
   *
   * The second action to ride the movement frame, on exactly the reasoning `duelChoice` rides
   * it: one optional field on a channel that is already parsed, already rate-limited and already
   * capped at MAX_INPUTS_PER_TICK beats a `split` message kind to budget, size and validate for
   * one boolean.
   *
   * It is an *edge* rather than a held state, which is the difference between it and `boost`: a
   * frame carrying it is one press and is spent by the next tick, so three frames on three
   * consecutive ticks are what take a cluster 2 -> 4 -> 8. The client therefore sends it on the
   * frame the button went down and not on the ones after — and, unlike `duelChoice`, must not
   * re-send it until it is acknowledged, because a re-sent split is a second split.
   *
   * Intent, not effect. The simulation refuses it silently for a duellist, for a cell under
   * SPLIT_MIN_MASS and for a cluster at MAX_CELLS_PER_PLAYER.
   */
  split: z.boolean().optional(),
  /**
   * "Eject now" — every cell of my cluster that can afford it shoots an oversized pellet out
   * along my heading, as bait.
   *
   * The third action on the movement frame, on the reasoning `split` rides it: one optional
   * boolean on a channel that is already parsed, already rate-limited and already capped at
   * MAX_INPUTS_PER_TICK. It is an *edge* exactly as `split` is — one frame carrying it is one
   * volley, spent by the next tick — so a client sends it on the frame the button went down and
   * must not re-send it until it is acknowledged, because a re-sent eject is a second eject.
   *
   * Intent, not effect. The simulation refuses it silently for a duellist, for a cell under
   * EJECT_MIN_MASS and for a room already holding MAX_EJECTED_PELLETS_PER_ROOM of them.
   */
  eject: z.boolean().optional(),
  /**
   * "Fire what I am holding" — spend the power-up in my inventory slot.
   *
   * The fourth action on the movement frame, on the reasoning `split` and `eject` ride it: one
   * optional boolean on a channel that is already parsed, already rate-limited and already capped
   * at MAX_INPUTS_PER_TICK. It is an *edge* exactly as those two are — one frame carrying it
   * spends at most one power-up — so a client sends it on the frame the button went down and must
   * not re-send it until it is acknowledged.
   *
   * It names no power-up, deliberately. What a player is holding is the *server's* record
   * (`PlayerState.powerUp`), replicated back as `heldPowerUp`; a client that named the power-up
   * to fire would be a client that could fire one it never picked up.
   *
   * Intent, not effect. The simulation refuses it silently for a duellist and for a player
   * holding nothing.
   */
  usePowerUp: z.boolean().optional(),
  /**
   * "Say this" — raise the canned saying with this id as a bubble over my blob.
   *
   * The fifth action on the movement frame, on the reasoning the four above it ride it: one
   * optional field on a channel that is already parsed, already rate-limited and already capped
   * at MAX_INPUTS_PER_TICK. It is an *edge* like `split`, `eject` and `usePowerUp` — one frame
   * carrying it is one bubble, spent by the next tick — rather than a held state like
   * `duelChoice`, so a client sends it on the frame the button went down and must not re-send it.
   *
   * **AN ID, NEVER TEXT, AND THAT IS THE WHOLE DESIGN.** The words are resolved server-side out
   * of the shared catalogue or out of this player's own *approved* submissions; a browser cannot
   * name a string, cannot name another player's custom saying, and cannot name one of its own
   * that a moderator has not passed. Widening this field to carry text would turn a canned-saying
   * feature into an unmoderated chat in one line.
   *
   * Intent, not effect. The simulation refuses it silently for a guest (who has no vocabulary at
   * all), for an id that resolves to nothing, and for a player still inside SAYING_COOLDOWN_TICKS
   * of their last one. A DUELLIST MAY SPEAK — see `RoomSim.stepSayings` for why that is the one
   * action a frozen player keeps.
   */
  saying: sayingIdSchema.optional(),
  /**
   * "Point at this slot" — make slot `selectSlot` of my inventory the one `usePowerUp` fires.
   *
   * The sixth action on the movement frame, and an *edge* like the four before it: one frame
   * carrying it is one switch, spent by the next tick whether or not anything moves.
   *
   * **IT NAMES AN INDEX AND NEVER A KIND, and that is `usePowerUp`'s rule one step along.** That
   * field names no power-up because a client that could name the one to fire could fire one it
   * never picked up; this one names no power-up because a client that could name the one to
   * *select* could select one it never picked up, which is the same cheat with an extra tick in
   * it. What the index points at is the server's record (`PlayerState.inventory`), replicated
   * back as `inventory`, and an index past the end of it is refused silently like every other
   * intent this frame carries.
   *
   * **SWITCHING AWAY FROM A TIMED POWER-UP DESTROYS IT** and switching away from a charged one
   * keeps every charge — the player asked for exactly that ("If switching during a timed power up,
   * then it basically just removes it and it's done. But if a multi use - it should remain with
   * the number of counts remaining"), and it is one branch on the registry's `UseModel` rather
   * than anything this schema knows. Selecting the slot you are already on is not a switch and
   * costs nothing, which is what stops a repeated press throwing away the window it is pointing at.
   *
   * Intent, not effect. The simulation refuses it silently for a duellist, for a frozen player and
   * for an index that names no slot.
   */
  selectSlot: z
    .number()
    .int()
    .min(0)
    .max(POWER_UP_INVENTORY_SIZE - 1)
    .optional(),
  /**
   * "This is my answer to the vote" — the id of one of the options the room is offering.
   *
   * The seventh action to ride the movement frame, on exactly the reasoning the six above it ride
   * it: one optional field on a channel that is already parsed, already rate-limited and already
   * capped at MAX_INPUTS_PER_TICK beats a `vote_answer` message kind to budget, size and validate
   * for one string.
   *
   * **AN OPTION ID, NEVER A LABEL AND NEVER AN INDEX**, which is `duelChoice`'s rule and
   * `selectSlot`'s read together: a client that could answer with words could answer with words
   * nobody offered, and one that answered by position would make a reordered ballot a different
   * vote. The simulation checks the value against the running vote's own option list.
   *
   * It is a *held* state rather than an edge, exactly as `duelChoice` is and for its reason: a
   * client re-sends the same value on every input frame until it has reason to believe the answer
   * landed, so a frame lost to the input cap costs a tick and not the vote. **The first answer
   * wins and later ones are refused**, which is `lockDuelChoice`'s rule and what makes re-sending
   * safe: a second answer would be a player watching the arena and changing their mind, and the
   * stake was snapshotted at the first one.
   *
   * Intent, not effect, and every refusal is silent: no vote open, an id that is not one of its
   * options, and a player who has already answered all simply do not answer again. **A DUELLIST
   * MAY ANSWER**, which is the sayings rule rather than the split's — being frozen is a statement
   * about the world, and a vote answer moves no body.
   */
  voteAnswer: z.string().min(1).max(MAX_VOTE_OPTION_ID_LENGTH).optional(),
  /**
   * "I am holding the sprint control" — move at SPRINT_SPEED_MULTIPLIER while the bar lasts.
   *
   * **IT IS A HELD LEVEL AND NOT AN EDGE, AND IT IS THE ONE FIELD ON THIS FRAME THAT DELIBERATELY
   * BREAKS THE RULE ALL THE OTHERS FOLLOW.** "One frame carrying it is one action" is what
   * `split`, `eject`, `usePowerUp`, `saying` and `selectSlot` all mean, and it is why each of them
   * says out loud that a client must not re-send it until it is acknowledged. This one is a
   * *state*, exactly as `dirX`/`dirY` are: a client sets it on **every** frame while the control
   * is down and stops setting it the moment the control comes up, and it is never acknowledged
   * because there is nothing to acknowledge.
   *
   * **A FRAME THAT DOES NOT CARRY IT MEANS NOT SPRINTING**, which makes a dropped frame end a
   * sprint rather than extend one — the safe failure, and the reason the surface is shaped this
   * way round. A frame that never arrives at all leaves the level where it was, which is exactly
   * what already happens to a heading, and it is bounded by the bar: a silent client sprints for
   * at most STAMINA_MAX / STAMINA_DRAIN_PER_SECOND seconds and then has nothing left to spend.
   *
   * `boost` at the top of this frame has always been shaped this way and has never been read by
   * anything; this is the first held field the simulation acts on.
   *
   * Intent, not effect. The simulation refuses it silently for a duellist, for a frozen player,
   * for a player who is not steering (a sprint that would move nothing costs nothing), for an
   * empty bar, and for a sprint that is not already running on a bar under
   * STAMINA_MIN_SPRINT — and a refusal *spends* the level rather than storing it, so a button
   * held through a duel does not fire on the tick the freeze lifts. What comes back is the
   * viewer's own `stamina` and, if anybody can see them, their id in `sprinting`.
   */
  sprint: z.boolean().optional(),

  /**
   * END THE LIVE ROUND NOW — an edge, and the only action on this frame a player with no body may
   * send.
   *
   * It is granted only while no human is still standing in a deathmatch round, which is the
   * server's own test (`RoomSim.requestEndRound`) rather than this field's: the client draws the
   * button off `humansAlive` on the match frame, and a client that ignored that would be asking
   * to end a round somebody else is winning. Refused in silence, like every other press.
   *
   * It names no round. Which round is being ended is the room's answer, `usePowerUp`'s rule.
   */
  endRound: z.boolean().optional(),

  /** The viewport shape again, re-sent whenever it changes — see `clientHelloSchema.aspect`. */
  aspect: z.number().positive().finite().optional(),

  /**
   * Where a SPECTATOR's camera is looking, in world units.
   *
   * **The one positional value the server ever takes from a client, and the only reason it is
   * acceptable is that the client it is taken from cannot act.** A spectator has no cells, is
   * never eaten, is never duelled and displaces nobody, so there is nothing here to cheat at:
   * the pair decides which slice of the room is replicated back to them and nothing else. It is
   * clamped to the room's walls by the gateway, and it is **ignored outright for a player** —
   * whose position is the simulation's, derived from `dirX`/`dirY` and nothing else, exactly as
   * it always was.
   *
   * Both or neither. A frame carrying one and not the other leaves the camera where it was,
   * because half a position is not a position.
   */
  camX: z.number().finite().optional(),
  camY: z.number().finite().optional(),
});

export const clientPingSchema = z.object({
  t: z.literal('ping'),
  clientTime: timestampSchema,
});

export const clientMessageSchema = z.discriminatedUnion('t', [
  clientHelloSchema,
  clientInputSchema,
  clientPingSchema,
]);

export type ClientHello = z.infer<typeof clientHelloSchema>;
export type ClientInput = z.infer<typeof clientInputSchema>;
export type ClientPing = z.infer<typeof clientPingSchema>;
export type ClientMessage = z.infer<typeof clientMessageSchema>;

// ---------------------------------------------------------------------------
// server -> client
// ---------------------------------------------------------------------------

export const PROTOCOL_ERROR_CODES = [
  'bad_message',
  'protocol_version_mismatch',
  'unauthorized',
  'room_full',
  'room_not_found',
  'name_rejected',
  'rate_limited',
  'server_error',
  /**
   * This account started playing somewhere else, and this is the connection that lost.
   *
   * Its own code rather than `unauthorized` or `room_full`, because it is neither: nothing is
   * wrong with the credential and nothing is wrong with the room. It is terminal for THIS socket
   * — redialling would be refused by the same rule — and the honest answer to it is a button that
   * joins again, which evicts the other one in turn. See `endOtherLiveGames` on the API server.
   *
   * Appended rather than inserted: the binary codec writes a code as its index in this array.
   */
  'replaced_elsewhere',
] as const;

export const protocolErrorCodeSchema = z.enum(PROTOCOL_ERROR_CODES);
export type ProtocolErrorCode = z.infer<typeof protocolErrorCodeSchema>;

export const serverWelcomeSchema = z.object({
  t: z.literal('welcome'),
  protocolVersion: z.number().int().nonnegative(),
  playerId: entityIdSchema,
  room: roomInfoSchema,
  /** Server clock at send time, so the client can seed its clock offset before the first ping. */
  serverTime: timestampSchema,
  /**
   * Whether this socket was seated as a SPECTATOR rather than as a player.
   *
   * The client already asked for one or the other, so this is a confirmation rather than news —
   * but it is the *server's* answer, taken off the signed ticket, and a client that drew a blob
   * for a `playerId` the simulation has never heard of would be drawing nothing forever without
   * ever being told why. A spectator's `playerId` is a synthetic id that names no player in the
   * room, deliberately: it appears in no snapshot, so "find myself in the frame" simply finds
   * nobody.
   */
  spectator: z.boolean(),
});

/** Full state of everything the receiving client can see. Sent on join and after a desync. */
export const serverSnapshotSchema = z.object({
  t: z.literal('snapshot'),
  tick: tickSchema,
  /** Last client input seq applied, for client-side reconciliation. */
  ackSeq: z.number().int().nonnegative(),
  players: z.array(playerSnapshotSchema),
  pellets: z.array(pelletSnapshotSchema),
  /**
   * Every spike in the room, not just the ones in view — the call `powerUps` makes below, for its
   * reasons: a spike is map furniture rather than information about a person, there are a couple
   * of dozen, and one across the map is worth knowing about before you drive at it.
   *
   * **It is no longer "only ever here".** A spike used to be a fixture placed once and never
   * moved, eaten or destroyed, so the delta carried no spike fields at all; it is consumed by the
   * cell it breaks now, respawns elsewhere, and can be launched by being fed — see
   * `serverDeltaSchema.spikes`, which is what keeps a client's set up.
   */
  spikes: z.array(spikeSnapshotSchema),
  /** Duels involving anyone this client can see. See the note on `serverDeltaSchema.duels`. */
  duels: z.array(duelSnapshotSchema),
  /**
   * The chosen photo of every visible player who has one. A baseline frame is the whole of what
   * a client can see, so this is the whole of it too; the delta then only mentions somebody who
   * has just come into view.
   */
  skins: z.array(playerSkinSchema),
  /**
   * Which of the visible players are bots. A baseline frame is the whole of what a viewer can
   * see, so this is the whole of it too — see `serverDeltaSchema.bots` for how it is kept up.
   */
  bots: z.array(entityIdSchema),
  /**
   * The country of every visible player who has one. A baseline frame is the whole of what a
   * client can see, so this is the whole of it too; the delta then only mentions somebody who
   * has just come into view.
   */
  countries: z.array(playerCountrySchema),
  /**
   * Every power-up orb in the room, not just the ones in view.
   *
   * Room-wide on purpose, and it is the same call `spikes` makes one field up: an orb is map
   * furniture rather than information about a person, there are at most
   * MAX_POWER_UPS_PER_ROOM of them, and being able to see one across the map on the minimap is
   * most of what makes it worth crossing the map for. Interest-culling four entities would cost
   * more to decide than to send, and would make an orb blink on and off the minimap as its
   * finder wandered.
   *
   * Unlike `spikes` it is not fixed for the room's life, so the delta carries it too — see
   * `serverDeltaSchema.powerUps`.
   */
  powerUps: z.array(powerUpSnapshotSchema),
  /**
   * What THIS viewer is carrying: the slots and which of them is selected. The one per-viewer
   * field on the frame besides `ackSeq`.
   *
   * Registry ids rather than anything drawable — the client looks the glyph and the name up in
   * `@chompdome/shared/powerups`, so a power-up's presentation is never on the wire — plus the two
   * numbers a registry cannot know because they change while a slot is held. See
   * `PlayerInventory`, and `clientInputSchema.selectSlot` for the edge that moves the index.
   *
   * It is sent on **every** frame rather than on the `powerup` divisor the orb field rides,
   * because it is the answer to "did that pickup register" and to "how much of my rocket window is
   * left": an empty inventory is one zero byte, so the frames that say nothing cost nothing.
   */
  inventory: playerInventorySchema,
  /**
   * Every projectile in the room, not just the ones in view — the same call `powerUps` makes one
   * field up, for the same reasons (see `ProjectileSnapshot`).
   *
   * Unlike `powerUps` it is not on a divisor and is never null: the `projectile` tier is 20 Hz
   * because a rocket is somewhere new every tick, so "no news" is not a state this list can be
   * in. An empty room's frame carries one zero byte for it.
   */
  projectiles: z.array(projectileSnapshotSchema),
  /**
   * The speech bubbles this viewer can see that are still running.
   *
   * Interest-scoped, unlike `spikes`, `powerUps` and `projectiles`: a bubble is drawn *on a blob*,
   * so a viewer who cannot see the speaker has nowhere to put it and no business being told about
   * it. A baseline frame is the whole of what a viewer can see, so this is the whole of it too;
   * the delta then only mentions a bubble that has just started or a speaker who has just walked
   * into view.
   */
  sayings: z.array(sayingSnapshotSchema),
  /**
   * Merged pairs among the players this viewer can see.
   *
   * Interest-scoped for `sayings`' reason — it is drawn *on a blob* — and re-stated in full on
   * every frame rather than diffed, because a merge is a state with an end rather than a fact
   * fixed for a player's stay: the frame that stops mentioning one is how a client learns it came
   * apart. `duels`' rule exactly. A zero-length varint in every room that is not a `teams` room.
   */
  merges: z.array(mergeSnapshotSchema),
  /**
   * Which of the visible players a freeze ray still has — see `serverDeltaSchema.frozen`. A
   * baseline frame is the whole of what a viewer can see, so this is the whole of it too.
   */
  frozen: z.array(entityIdSchema),
  /**
   * Which of the visible players a frost patch still has — see `serverDeltaSchema.slowed`. A
   * baseline frame is the whole of what a viewer can see, so this is the whole of it too.
   */
  slowed: z.array(entityIdSchema),
  /**
   * Which of the visible players are hidden — see `serverDeltaSchema.invisible`. In practice that
   * is the viewer or nobody, because a hidden player is on no other viewer's frame.
   */
  invisible: z.array(entityIdSchema),
  /**
   * Which of the visible players are still inside their spawn shield — see
   * `serverDeltaSchema.spawnProtected`. A baseline frame is the whole of what a viewer can see, so
   * this is the whole of it too.
   */
  spawnProtected: z.array(entityIdSchema),
  /**
   * Every prize shard in the room, not just the ones in view.
   *
   * Room-wide on `powerUps`' own argument, one field up: a shard is map furniture rather than
   * information about a person, there is a handful of them, and being able to see one from across
   * the arena on the minimap is most of what makes crossing the arena for it worth doing. A
   * baseline is the whole of what this viewer can see, so it carries the room's whole set whether
   * or not this tick is a powerup one — see `serverDeltaSchema.prizeShards` for the divisor the
   * delta rides.
   */
  prizeShards: z.array(prizeShardSnapshotSchema),
  /**
   * Every star in the room — at most MAX_STARS_PER_ROOM of them, and usually none at all.
   *
   * Room-wide on `prizeShards`' and `powerUps`' argument, one field up: a star is map furniture
   * and a *destination*, and being able to see the rarest thing on the map from across the arena
   * is the whole of what makes crossing it worth doing. A baseline carries the room's whole set
   * whether or not this tick is a powerup one — see `serverDeltaSchema.stars` for the divisor the
   * delta rides.
   */
  stars: z.array(starSnapshotSchema),
  /**
   * Which of the visible players are sprinting — see `serverDeltaSchema.sprinting`. A baseline
   * frame is the whole of what a viewer can see, so this is the whole of it too.
   */
  sprinting: z.array(entityIdSchema),
  /**
   * How much sprint THIS viewer has left, as a whole percentage point of STAMINA_MAX — see
   * `serverDeltaSchema.stamina`. Zero for a spectator, who has no bar and no body to spend one on.
   */
  stamina: z.number().int().min(0).max(STAMINA_MAX),
});

/**
 * Incremental update against `baseTick`.  projectiles: z.array(projectileSnapshotSchema),
});

/**
 * Incremental update against `baseTick`. Players are sent whole (they are small and change every
 * tick); pellets are event-driven, so they only appear here when spawned or eaten.
 */
export const serverDeltaSchema = z.object({
  t: z.literal('delta'),
  tick: tickSchema,
  baseTick: tickSchema,
  ackSeq: z.number().int().nonnegative(),
  players: z.array(playerSnapshotSchema),
  removedPlayers: z.array(entityIdSchema),
  pelletsAdded: z.array(pelletSnapshotSchema),
  pelletsRemoved: z.array(entityIdSchema),
  /**
   * Every duel this client can see, in full, every frame — no `duelsRemoved` counterpart.
   *
   * Duels are rare, tiny and short-lived, and a duel that has ended is one this list simply
   * stops mentioning; the client replaces its whole set from each frame. That costs an empty
   * array on a quiet frame and is worth it, because a duel diff that ever drifted would leave a
   * player frozen behind a UI that thinks the fight is over.
   */
  duels: z.array(duelSnapshotSchema),
  /**
   * Skins for the players in `players` that this viewer had not been told about on the previous
   * frame — which, on a quiet frame, is nobody. This is the whole reason a skin is not a field
   * of `playerSnapshotSchema`: it is fixed for a player's stay in the room, so re-sending it 20
   * times a second to every viewer would spend the tightest budget in the game on a constant.
   *
   * There is no `skinsRemoved`: a skin is dropped with the player it belongs to (`removedPlayers`),
   * and somebody who walks back into view is simply named here again.
   */
  skins: z.array(playerSkinSchema),
  /**
   * The ids of the players in `players` that this viewer had not been told about on the previous
   * frame and that are bots — which, on a quiet frame, is nobody.
   *
   * Being a bot is fixed for a player's stay in the room, exactly as its skin is, so it travels
   * on the same channel and for the same reason: a bit *per player per tick* would cost a byte
   * on the tier that already caps players-per-core, and there is no spare flags byte on a player
   * frame to hide it in (a player is an id, a name, a hue and its cells). Named once, this costs
   * a two-byte id when a bot walks into view and a single zero-length varint on every one of the
   * twenty frames a second after it.
   *
   * No `botsRemoved`, for the same reason `skins` has no counterpart: the flag is dropped with
   * the player it belongs to (`removedPlayers`), and somebody who walks back into view is simply
   * named here again.
   */
  bots: z.array(entityIdSchema),
  /**
   * Countries for the players in `players` that this viewer had not been told about on the
   * previous frame — which, on a quiet frame, is nobody.
   *
   * Same channel and same rule as `skins`, because it is the same kind of value: fixed for a
   * player's stay in the room, so re-sending it twenty times a second to every viewer would
   * spend the tightest budget in the game on a constant. Named once, it costs a two-byte id and
   * two letters when somebody walks into view and a single zero-length varint on every frame
   * after it. No `countriesRemoved`: it is dropped with the player (`removedPlayers`).
   */
  countries: z.array(playerCountrySchema),
  /**
   * The room's whole set of power-up orbs, or null.
   *
   * THE ONLY FIELD ON THIS FRAME THAT RIDES A TIER RATHER THAN AN EVENT, and that is what
   * `UPDATE_RATE_HZ.powerup` reserved. It is populated on the ticks `shouldUpdate('powerup',
   * tick)` names — 5 Hz — and is `null` on the other three frames out of four, which is "this
   * frame says nothing about orbs" and is a different value from `[]`, which says the room has
   * none. A client replaces its whole set from a non-null list and leaves it alone otherwise.
   *
   * Sent whole rather than as added/removed lists, for the reason `duels` is: there are at most
   * MAX_POWER_UPS_PER_ROOM of them, so the whole set is a handful of bytes, and a diff that ever
   * drifted would leave an orb painted on a client's minimap that nothing could ever clear. It
   * is also identical for every viewer in the room, so it is built once per room per powerup
   * tick and shared, exactly like the leaderboard's top ten.
   */
  powerUps: z.array(powerUpSnapshotSchema).nullable(),
  /**
   * The room's whole set of spikes, or null — **and this field is new, because a spike used to be
   * fixed for the room's life and is not any more.**
   *
   * It is consumed by the cell it breaks, comes back somewhere else SPIKE_RESPAWN_TICKS later,
   * and can be launched across the map by being fed (see `SPIKE_FEED_COUNT`) — so the snapshot's
   * "only ever here" is gone and the delta has to be able to say what moved.
   *
   * **IT IS ON NO TIER AT ALL, which is where it parts company with `powerUps` one field up.** A
   * spike field changes on the ticks something happens to it and on no others: null on every
   * other frame, so a room where nobody has touched a spike pays the single byte a null costs,
   * and the whole set on the frames a spike is broken, respawns or moves. A burst spike moves at
   * 20 Hz and a 5 Hz divisor would draw it stuttering across the map; a divisor would also mean a
   * consumed spike stayed on screen for up to three frames after the shatter that took it, which
   * is exactly the frame a player is looking at it.
   *
   * Sent whole rather than diffed, for `powerUps`' reason and harder: there are a couple of dozen
   * of them, and a diff that drifted would leave a hazard painted on a client that nothing could
   * clear — which in this case is a hazard they would steer around for the rest of the round.
   * Identical for every viewer, so it is built once per room and shared.
   */
  spikes: z.array(spikeSnapshotSchema).nullable(),
  /** What this viewer is carrying — see `serverSnapshotSchema.inventory`. */
  inventory: playerInventorySchema,
  /**
   * The room's whole set of projectiles, every frame.
   *
   * Whole rather than diffed for the reason `duels` and `powerUps` are — there are at most
   * MAX_PROJECTILES_PER_ROOM, and a diff that drifted would leave a mine painted on a client's
   * screen that nothing could ever clear — and every frame rather than on a divisor because these
   * are the only room-wide entities that move. Not nullable: a 20 Hz tier has no off-frames to
   * say "no news" on. Built once per room per tick and shared by every socket in it, exactly like
   * the orb list.
   */
  projectiles: z.array(projectileSnapshotSchema),
  /**
   * The bubbles this viewer has not been told about yet: one entry per visible speaker whose
   * bubble started after the last frame this socket was sent, or who was not in view on it.
   *
   * The `skins` channel's rule, applied to something that is *not* fixed for a player's stay — so
   * the test is on the bubble rather than only on the player. A bubble is a rare event with a
   * known end (`endsAtTick`), which is what makes "send it once" safe: nothing has to retract one,
   * and there is no `sayingsRemoved` because a client expires it on its own clock. A speaker who
   * leaves takes it with them through `removedPlayers`.
   *
   * On a quiet frame this is a single zero-length varint, which is the whole cost of the feature
   * to a room where nobody is talking.
   */
  sayings: z.array(sayingSnapshotSchema),
  /**
   * Merged pairs among the players this viewer can see.
   *
   * Interest-scoped for `sayings`' reason — it is drawn *on a blob* — and re-stated in full on
   * every frame rather than diffed, because a merge is a state with an end rather than a fact
   * fixed for a player's stay: the frame that stops mentioning one is how a client learns it came
   * apart. `duels`' rule exactly. A zero-length varint in every room that is not a `teams` room.
   */
  merges: z.array(mergeSnapshotSchema),
  /**
   * Which of the visible players a freeze ray still has, in full, every frame — no
   * `frozenRemoved` counterpart.
   *
   * `duels`' rule rather than `bots`': a freeze is a *state with an end*, not a fact fixed for a
   * player's stay, so it is re-stated every frame and the client replaces its whole set from each
   * one. A player who thaws is one this list simply stops naming, which is what makes it
   * impossible for a rime ring to be left painted on somebody nothing can clear.
   *
   * Interest-scoped, unlike `powerUps` and `projectiles`, for the reason `sayings` is: it is
   * drawn *on a blob*, so a viewer who cannot see the player has nowhere to put it.
   *
   * A bare list of ids rather than a shape with a deadline on it, which is where it parts company
   * with `duels`: nothing on the client counts a freeze down — there is no overlay and no timer —
   * so the end tick would be a field on the wire that nobody reads.
   */
  frozen: z.array(entityIdSchema),
  /**
   * Which of the visible players a frost patch still has at half speed, in full, every frame — no
   * `slowedRemoved` counterpart.
   *
   * `frozen`'s field, in every respect and for every one of `frozen`'s reasons: a slow is a state
   * with an end rather than a fact fixed for a player's stay, it is drawn *on a blob* so it is
   * interest-scoped, and nothing on this client counts it down so a deadline would be a field
   * nobody reads.
   *
   * **A SEPARATE LIST RATHER THAN A WIDENING OF `frozen`**, because the two statuses compose: a
   * player can be inside both deadlines at once, the deadlines end at different ticks, and the
   * renderer draws them differently — a rime ring that says "cannot act" against a frost sheen
   * that says "can, but slowly". Folding them into one list would make a thawed-but-still-slowed
   * player indistinguishable from a free one, which is precisely the player who most needs to be
   * told.
   */
  slowed: z.array(entityIdSchema),
  /**
   * Which of the visible players an invisibility power-up still has, in full, every frame — no
   * `invisibleRemoved` counterpart.
   *
   * `frozen`'s and `slowed`'s field in shape and for their reasons: a status with an end rather
   * than a fact fixed for a player's stay, so it is re-stated every frame and a player who comes
   * back is one this list simply stops naming; a bare list of ids, because nothing on this client
   * counts it down.
   *
   * **IT IS THE ONE OF THE THREE THAT CAN ONLY EVER NAME THE VIEWER**, and that is the feature
   * rather than a quirk of it. A hidden player is removed from every *other* viewer's interest set
   * outright (`selectInterest`), so no frame on any other socket carries their position, their
   * mass or their id — this list exists so that the person who pressed the button can be shown
   * their own state, and so the renderer can draw it composed with their outline, hue and photo
   * rather than instead of them. There is deliberately no other channel that leaks it: the
   * minimap is drawn from this same interest set, and the leaderboard and the roster carry no
   * position at all.
   */
  invisible: z.array(entityIdSchema),
  /**
   * Which of the visible players cannot be touched yet because they have only just joined, in
   * full, every frame — no `spawnProtectedRemoved` counterpart.
   *
   * `frozen`'s, `slowed`'s and `invisible`'s field in shape and for their reasons: a status with
   * an end rather than a fact fixed for a player's stay, so it is re-stated every frame and a
   * player whose shield is over is one this list simply stops naming; a bare list of ids, because
   * nothing on this client counts it down. A **fourth** list rather than a flag folded into one of
   * them, because all four compose, end at different ticks and are drawn differently.
   *
   * **IT IS THE OPPOSITE OF `invisible` IN THE ONE RESPECT THAT MATTERS.** That list can only name
   * the viewer, because a hidden player is taken off every other viewer's interest set; this one
   * names anybody the viewer can see, and that is the feature rather than an oversight — the
   * player report asks for the circle to be *shown*, throbbing, so that the rest of the room reads
   * "do not bother" and goes and eats somebody else. `selectInterest` does not read the deadline
   * behind this field and must never start to.
   */
  spawnProtected: z.array(entityIdSchema),
  /**
   * The room's whole set of prize shards, or null.
   *
   * **THE SECOND FIELD ON THIS FRAME THAT RIDES A TIER RATHER THAN AN EVENT, and it rides
   * `powerUps`' one.** A shard does not move — it is scattered once and lies there until somebody
   * eats it or PRIZE_SHARD_LIFETIME_TICKS runs out — so there is nothing for a 20 Hz tier to say
   * about it that a 5 Hz one cannot. It is populated on the ticks `shouldUpdate('powerup', tick)`
   * names and is `null` on the other three frames in four, which is "this frame says nothing about
   * shards" and is a different value from `[]`, which says the room has none. A client replaces
   * its whole set from a non-null list and leaves it alone otherwise; getting those two the wrong
   * way round would either freeze a shard on the minimap forever or blink every one of them off
   * three frames in four.
   *
   * Sent whole rather than as added/removed lists, and shared by every socket in the room, for
   * every one of `powerUps`' reasons. What it is *worth* is not here — see
   * `PrizeShardSnapshot`.
   */
  prizeShards: z.array(prizeShardSnapshotSchema).nullable(),
  /**
   * The room's whole set of stars, or null — `prizeShards`' field one line up, in every respect
   * and for every one of its reasons. A star does not move either, so it rides the same 5 Hz
   * `powerup` divisor, `null` is "this frame says nothing about stars" and `[]` is "there is no
   * star on the field", and the list is replaced whole rather than diffed.
   *
   * A separate list rather than a widening of `prizeShards`, because the two are different
   * entities with different radii, different rules about who may take one and — the one that
   * decides it — different consequences for taking one.
   */
  stars: z.array(starSnapshotSchema).nullable(),
  /**
   * Which of the visible players are sprinting, in full, every frame — no `sprintingRemoved`
   * counterpart.
   *
   * `frozen`'s, `slowed`'s, `invisible`'s and `spawnProtected`'s field in shape and for every one
   * of their reasons: a status with an end rather than a fact fixed for a player's stay, so it is
   * re-stated every frame and somebody who lets go of the control is one this list simply stops
   * naming; interest-scoped, because it is drawn *on a blob*; a bare list of ids, because nothing
   * on the client counts a sprint down — the bar it would count is the viewer's own and is on
   * `stamina` below. A **fifth** list rather than a flag folded into one of them, because all five
   * compose, end at different moments and are drawn differently.
   *
   * **IT IS THE ONLY ONE OF THE FIVE THAT EXISTS TO EXPLAIN A SPEED**, and that is why it is on
   * the wire at all rather than being kept private like the bar. A sprint is the one thing in this
   * game that makes a blob outrun what its mass says it can do, and a blob doing that with nothing
   * on it to say why reads as a bug or as a cheat — which is the one thing a speed change must
   * never look like. What it does *not* say is how much sprint that player has left: knowing
   * whether somebody else can still run is not information this game gives you.
   */
  sprinting: z.array(entityIdSchema),
  /**
   * How much sprint THIS viewer has left: a whole percentage point of STAMINA_MAX, which is 100,
   * so the number on the wire is the number a meter draws.
   *
   * **THE VIEWER'S OWN AND NOBODY ELSE'S, which is `inventory`'s rule rather than `sprinting`'s
   * one field up.** There is deliberately no stamina on `PlayerSnapshot`: that would be a byte per
   * player per tick on the tier that already caps players-per-core, in order to publish something
   * this game does not tell you about anybody else. One varint on the frame the viewer was already
   * being sent is the whole cost.
   *
   * Quantised rather than sent as the float the simulation holds, on `DuelSnapshot.pot`'s
   * argument: a point of this bar is 40 ms of sprint, which is under one tick, so the difference
   * is below anything a player could act on — and a percentage a client has to divide for is a
   * client that can disagree with the server about what full looks like. Sent on **every** frame
   * rather than on a divisor, for `inventory`'s reason: it is the answer to "why did I stop", and
   * a bar that lagged 150 ms behind the button would read as the control not working.
   *
   * Zero for a spectator, who has no bar and no body to spend one on — an absent field would be
   * the same statement in a shape every reader has to branch on.
   */
  stamina: z.number().int().min(0).max(STAMINA_MAX),
});

export const serverLeaderboardSchema = z.object({
  t: z.literal('leaderboard'),
  tick: tickSchema,
  /**
   * How the room's seats are split between people and AI, right now.
   *
   * On this frame rather than on `welcome` because it *moves* — `stepBotFill` seats and retires
   * bots as humans come and go — and on this frame rather than one of its own because the
   * leaderboard is already the room-wide 2 Hz channel, which is as often as a count of players
   * is worth re-drawing. It is the same split the operator console reads as
   * `RoomSummary.humanCount` / `.botCount`.
   */
  humanCount: z.number().int().nonnegative(),
  botCount: z.number().int().nonnegative(),
  entries: z.array(leaderboardEntrySchema),
  /**
   * This viewer's own place, and the only per-viewer field on the frame.
   *
   * Null whenever the viewer is already in `entries` — a player is never sent twice, so a client
   * has no duplicate to reconcile — and null for a spectator the room has no player for.
   */
  self: leaderboardSelfSchema.nullable(),
});

/**
 * WHO IS IN THE ROOM — every player, by name, with their bot flag and their flag.
 *
 * The widest frame on the wire and the slowest, and the two facts are the same decision. Three
 * things make it affordable, and none of them is optional:
 *
 * 1. **It is room-wide, so it is built once and encoded once.** Every socket in the room is
 *    handed the identical `WireData` (`BroadcastRegistry.replicateRoom`) — the leaderboard's top
 *    ten's own arrangement. There is no per-viewer field on it, deliberately: `leaderboard.self`
 *    is what a per-viewer field costs, an extra encode for every viewer the shared frame did not
 *    already name, and a roster is two orders of magnitude too wide to pay that twice.
 * 2. **It is its own frame rather than a field on `leaderboard`**, for exactly that reason. A
 *    nullable `roster` on that frame would have ridden through `sendLeaderboard`, which re-encodes
 *    the whole frame per viewer outside the top ten — which is nearly everybody.
 * 3. **It is 1 Hz** (`UPDATE_RATE_HZ.roster`), the slowest tier there is.
 *
 * WHY IT EXISTS AT ALL: a client is replicated its own interest set and nothing wider, so a list
 * built from `snapshot`/`delta` would name a fraction of the room and would gain and lose people
 * as the viewer walked around. `bots` and `countries` are interest-scoped for the same reason and
 * cannot answer for anybody outside it. This is the channel that can.
 *
 * **IT CARRIES A COARSE POSITION NOW, AND THAT REVERSES THE RULE THIS HEADER USED TO STATE IN
 * CAPITALS.** It said there was no position on it and there must never be one, because knowing
 * somebody is in the room is not knowing where they are. Andy asked for the opposite outright —
 * "the minimap should show ALL players on the map at all times" — so `RosterEntry.x`/`.y` are here
 * and the reversal has a shape rather than being a hole:
 *
 * - **Quantised to MINIMAP_POSITION_STEP**, which is under two pixels on the panel it feeds and
 *   nowhere near enough to aim with. The channel can say "somebody is over there" and cannot be
 *   made to say "somebody is exactly there", so it does not undo the interest management it sits
 *   beside — every *cell* a viewer can actually see is still culled to their own set.
 * - **Still 1 Hz**, so a dot is a strategic fact rather than a tracking shot.
 * - **Matched by what a bot knows**: a bot with nothing in its own perception now heads for the
 *   nearest player anywhere, off the same room-wide knowledge. Giving one side the map and not
 *   the other is what would have been unfair.
 *
 * Everything else on it still leaks nothing a player could not read off the leaderboard and the
 * population badge, and there is still no mass, no cell count and nothing about what anybody is
 * holding: a dot on a map, and no more than that.
 */
export const serverRosterSchema = z.object({
  t: z.literal('roster'),
  tick: tickSchema,
  /**
   * Everybody, bots included, in the room's own iteration order.
   *
   * Bounded by ROOM_MAX_PLAYERS — the simulation's hard ceiling — so a forged frame cannot make
   * a client build an unbounded list. Unsorted on the wire: the reader groups it, and a room that
   * re-sorted every second would make the panel jump under a finger.
   */
  entries: z.array(rosterEntrySchema).max(ROOM_MAX_PLAYERS),
});

/**
 * THE ROOM'S EVENT FEED — what just happened to whom, in words the room resolved.
 *
 * ITS OWN FRAME, for `serverRosterSchema`'s three reasons and a fourth of its own:
 *
 * 1. **It is room-wide, so it is built once and encoded once.** Every socket in the room is handed
 *    the identical `WireData` (`BroadcastRegistry.replicateRoom`). There is no per-viewer field on
 *    it, deliberately — `leaderboard.self` is what one costs, an extra encode for every viewer the
 *    shared frame did not name.
 * 2. **Room-wide is also the point, not a saving.** A pickup by somebody a viewer cannot see is
 *    still worth being told about; the `sayings` / `frozen` / `bots` channels are interest-scoped
 *    precisely because what they carry is drawn *on a blob*, and a feed is not. This is the roster
 *    frame's argument applied to events rather than to people.
 * 3. **It carries no position, and there must never be one on it.** Knowing somebody took an orb
 *    is not knowing where they took it. Names, the bot flag and a power-up id are all things the
 *    leaderboard, the roster and the orb list already put on the wire, so this leaks nothing new.
 * 4. **IT IS ON NO SCHEDULE AT ALL**, which is where it goes further than the roster. There is a
 *    frame on a tick something happened and nothing whatever on the others — not an empty list, no
 *    frame — so a room where nobody is doing anything interesting pays *nothing*, rather than the
 *    zero-length varint an empty list on an existing frame would have cost every viewer every
 *    tick. `notices` is therefore non-empty by construction: an empty notice frame is not a thing.
 *
 * Bounded by MAX_NOTICES_PER_FRAME, which is the cap per room per tick as well as per frame,
 * because there is at most one of these per room per tick.
 */
export const serverNoticeSchema = z.object({
  t: z.literal('notice'),
  tick: tickSchema,
  notices: z.array(gameNoticeSchema).min(1).max(MAX_NOTICES_PER_FRAME),
});

/**
 * THE ROOM-WIDE VOTE — a question, its answers, its deadline, and afterwards what it decided.
 *
 * ITS OWN FRAME, for `serverRosterSchema`'s three reasons and `serverNoticeSchema`'s fourth:
 *
 * 1. **It is room-wide, so the shared part is built once and encoded once.** Every socket in the
 *    room is handed the identical `WireData` — a spectator and a dead player watching the arena
 *    included, because what the room is deciding is exactly what somebody watching came to look
 *    at.
 * 2. **Room-wide is the point rather than a saving.** Everybody in the room is offered the vote,
 *    so an interest-scoped channel could not deliver it — the roster frame's argument, applied to
 *    a question instead of to people.
 * 3. **It carries no position and there must never be one on it.** Knowing that the room is
 *    voting is not knowing where anybody is standing.
 * 4. **IT IS ON NO TIER AT ALL.** There is a frame on the tick a vote opens and a frame on the
 *    tick it resolves, and nothing whatever in between or afterwards — so a room where nobody has
 *    called a vote pays *nothing*, rather than the zero-length varint a nullable field on an
 *    existing frame would have cost every viewer every tick. The open frame is re-sent to a
 *    socket that has not been handed it yet (a player who joined mid-vote, a socket that was
 *    backed up when it went out), which is the same shared payload rather than a new one.
 *
 * **AN OPEN VOTE SAYS NOTHING ABOUT WHO HAS ANSWERED.** `result` is null while it is running and
 * there is no tally, no count and no list of voters on the frame at all — `toDuelSnapshot`'s
 * secrecy rule applied to a room instead of to a pair, because a running count is a running
 * instruction to join whichever side is winning. It is pinned against a real encoded frame in
 * `binary-codec.test.ts`, not asserted by inspection.
 *
 * **`self` IS THE ONE PER-VIEWER FIELD, AND IT IS `leaderboard.self`'s ARRANGEMENT AND ITS
 * PRICE.** It is null on the shared frame and null for everybody who did not vote; a voter who
 * was still in the world at resolution is sent the same frame with their own row filled in, which
 * is a different payload and therefore its own encode. That is one small extra encode per voter,
 * **once**, on the single tick a vote resolves on — against the leaderboard's, which pays the
 * same price twice a second forever.
 */
export const serverVoteSchema = z.object({
  t: z.literal('vote'),
  tick: tickSchema,
  /** `v<n>`, minted by the room. Stable for the vote's life and what a client keys its card on. */
  id: entityIdSchema,
  prompt: z.string().min(1).max(MAX_VOTE_PROMPT_LENGTH),
  options: z.array(voteOptionSchema).min(MIN_VOTE_OPTIONS).max(MAX_VOTE_OPTIONS),
  /**
   * The tick answers stop being taken on. A client counts down against its own last-known tick,
   * exactly as it does for a duel's `endsAtTick`, so there is no separate clock to drift.
   */
  endsAtTick: tickSchema,
  /** Null while the vote is open; see the secrecy note above. */
  result: voteResultSchema.nullable(),
  /** Null on the shared frame and for every viewer who was not part of the settlement. */
  self: voteSelfSchema.nullable(),
  /**
   * **"YOU ARE THIS VOTE'S STAR-TAKER."** False on the shared frame and for everybody else.
   *
   * The second per-viewer field on the frame and `self`'s arrangement exactly — one extra encode
   * for exactly one socket, twice in a vote's life — but it is on the **open** frame as well as
   * the settled one, which `self` is not, and it has to be: the taker's choice must be made before
   * the result is known, so the one client that draws a different card has to know while the vote
   * is still running.
   *
   * **A BOOLEAN RATHER THAN THE TAKER'S ID, and that is deliberate.** This frame carries no entity
   * ids apart from the vote's own and there is nothing on the receiving end that would look one up
   * — the notice channel's rule, which is why a notice names a person rather than pointing at one.
   * "Is it me" is the only question this field exists to answer, and who it actually was is a
   * *sentence*, which the `star_taken` notice already carries by name to the whole room.
   */
  star: z.boolean(),
});

/**
 * The phase, as the wire states it — see `MatchPhase` for the three and why a classic room has
 * none of them.
 *
 * Written out here rather than imported as a tuple, exactly as `gameNoticeSchema`'s kinds are:
 * this is the shape of the message, and the *ordering* the binary codec indexes into is a
 * property of the wire format that lives beside the codec. The `satisfies` is what stops this
 * list drifting past the type in the direction a schema can drift on its own.
 */
const matchPhaseSchema = z.enum(['waiting', 'live', 'ended']) satisfies z.ZodType<MatchPhase>;

/**
 * WHICH ROUND THIS ROOM IS IN — a deathmatch room's phase, its clock, its counts and its winner.
 *
 * ITS OWN FRAME, for `serverRosterSchema`'s three reasons and `serverNoticeSchema`'s fourth, and
 * it is `serverVoteSchema`'s arrangement throughout because it is the same shape of problem: an
 * occasional room-wide statement that every socket needs the same copy of.
 *
 * 1. **It is room-wide, so the shared part is built once and encoded once.** Every socket in the
 *    room is handed the identical `WireData` — a spectator, a dead player watching the arena and
 *    an entrant with no body at all included, because which round the room is in is exactly what
 *    all three of them are waiting to be told.
 * 2. **Room-wide is the point rather than a saving.** A round is the room's, not a viewer's: an
 *    interest-scoped channel could not deliver "somebody won" to the people who died first, who
 *    are most of the room by the time there is a winner.
 * 3. **It carries no position and no entity id, and there must never be one on it.** The notice
 *    channel's rule: this is a *statement about the room*, nothing on the receiving end looks its
 *    subject up, and an id would be the one thing on it a reader could join against a position.
 *    That is why the winner is a name plus `youWon` rather than a `winnerId` — see below.
 * 4. **IT IS ON NO TIER AT ALL.** There is a frame on the ticks something on it actually moved —
 *    the phase, the round, one of the three counts, the winner — and nothing whatever on the
 *    others. So a classic room pays *nothing*, because it has no match loop and never produces
 *    one; a deathmatch room sitting in an empty lobby pays nothing; and a live round pays a
 *    frame per elimination rather than a frame per tick. A socket that arrived after the last one
 *    went out is handed the same shared payload, which is `serverVoteSchema`'s re-send exactly.
 *
 * **`endsAtTick` IS THE ROOM'S DEADLINE AND THE COUNTDOWN IS THE BROWSER'S** — `VoteCountdown`'s
 * rule and a duel's before it. The room owns the tick a phase ends on and the client counts down
 * against its own last-known tick, so there is no second clock to drift and no reason to re-send
 * this frame while nothing about it has changed.
 *
 * **`youWon` IS THE ONE PER-VIEWER FIELD, AND IT IS `serverVoteSchema.star`'s ARRANGEMENT AND ITS
 * PRICE**: false on the shared payload, and one extra encode for exactly one socket, once per
 * round. It is here because **a name is not an identity**. Display names are not unique across
 * guests and bots, so a client that matched `winnerName` against its own would be guessing —
 * `NoticeOverlay`'s stated reason for having no `self` flag at all, arrived at from the other
 * side. A `winnerId` would be exact and cost no extra encode, and it is refused by rule 3: it is
 * the joinable handle this channel exists not to carry, where the boolean answers the only
 * question a client actually asks of the field.
 *
 * **AND THERE IS DELIBERATELY NO SECOND PER-VIEWER FIELD FOR THE VIEWER'S OWN STANDING** —
 * waiting, alive, or eliminated. Every one of those is already derivable from frames this client
 * has: `welcome.spectator` says it is watching rather than playing, a body in its own frame says
 * it is alive (a viewer is always in its own interest set), and a `died` frame this round is what
 * separates "eliminated" from "arrived mid-round and waiting for the next one". Paying for it
 * would cost an extra encode for **every socket that is not alive**, which during a lobby is all
 * of them and by the end of a round is most of them — the price `serverRosterSchema` refuses,
 * against `star`'s which is one socket.
 */
export const serverMatchSchema = z.object({
  t: z.literal('match'),
  tick: tickSchema,
  /** Which half of the loop the room is in — see `MatchPhase`. */
  phase: matchPhaseSchema,
  /**
   * The tick this phase ends on, or **null for a phase with no deadline** — which is two
   * different situations and deliberately one value: a lobby with nobody in it is not counting
   * down at all, and a live round runs until somebody has won it rather than until a clock runs
   * out.
   *
   * Null rather than the simulation's own 0, because 0 is a legal tick and a client handed one
   * would draw a deadline that expired at the start of the room. It costs the same single byte
   * either way — see the codec.
   */
  endsAtTick: tickSchema.nullable(),
  /**
   * The round most recently started: 1-based, and **0 before the first one has begun**. During
   * `ended` it names the round that has just been played rather than the one being gathered for.
   */
  round: z.number().int().nonnegative(),
  /**
   * How many humans are enrolled and owed a body by the next round.
   *
   * During a lobby that is everybody waiting for it to start; during a live round it is the
   * players it has already put out, plus anybody who arrived while it was running. Bounded by
   * ROOM_MAX_PLAYERS — the simulation's hard ceiling, which no legal frame can be over.
   */
  waiting: z.number().int().nonnegative().max(ROOM_MAX_PLAYERS),
  /** How many the round is waiting for: the room's seat cap, which is what fills its lobby. */
  capacity: z.number().int().positive().max(ROOM_MAX_PLAYERS),
  /**
   * How many bodies are still standing in the round, so the HUD can say "7 alive". Zero during a
   * lobby, when nobody has one.
   *
   * Every body, bots included, because being the last one standing has never asked which kind of
   * player a body belongs to — which is also why a bot can be the winner named below.
   */
  alive: z.number().int().nonnegative().max(ROOM_MAX_PLAYERS),
  /**
   * How many of those bodies are HUMAN — never more than `alive`, and zero outside `live`.
   *
   * **It exists so a round nobody has a stake in can be ended**: once this reaches 0 the players
   * still standing are all software, and every human in the room is either out or waiting for the
   * next round. The HUD offers `endRound` on exactly that, and the server checks it again.
   *
   * A client genuinely cannot work it out: the `bots` channel is interest-scoped and names only
   * the players a viewer can see, and the roster frame says who is IN the room rather than who is
   * still up.
   */
  humansAlive: z.number().int().nonnegative().max(ROOM_MAX_PLAYERS),
  /**
   * The two sides' scoreboard in a `teams` room, and **null in every other kind of room** — which
   * is what makes a deathmatch room's frame byte for byte what it was.
   *
   * Andy: "we will also need total mass numbers and players remaining for each team." Both of
   * those are room-wide facts a client cannot derive: the `bots` channel and every player list on
   * the frame are interest-scoped, so a viewer can only ever see part of a side.
   *
   * Always **both** sides when it is present, in `TEAM_IDS`' order, so a reader never has to
   * decide what a missing side means — a wiped-out team is a row of zeroes, which is exactly what
   * it is.
   */
  teams: z
    .array(
      z.object({
        team: z.enum(TEAM_IDS),
        /** Total mass the side is holding, rounded — the number the clock decides the round on. */
        mass: z.number().int().nonnegative(),
        /** Bodies the side has left. Zero is a side that has been wiped out. */
        alive: z.number().int().nonnegative().max(ROOM_MAX_PLAYERS),
      }),
    )
    .length(2)
    .nullable(),
  /**
   * Which side THIS viewer is on, or null — for a spectator, for somebody waiting out a round,
   * and for every player in a room that is not a `teams` room.
   *
   * The one per-viewer field on this frame besides `youWon`, and `youWon`'s price: one extra
   * encode for one socket. It is what lets the HUD say which row of the scoreboard is *yours*;
   * the blob's colour is what says it in the arena.
   */
  yourTeam: z.enum(TEAM_IDS).nullable(),
  /**
   * Who won the round `round` names, or null while one is being gathered for or played — and
   * **null for a round that ended with nobody left in it**, which is a real outcome rather than a
   * bug: a simultaneous double kill and the last two players disconnecting both produce one.
   *
   * A name and never an id, for rule 3 above. Bounded by the same 64 the player frame and the
   * roster bound a name at, so this schema never becomes the thing that refuses a frame a
   * `PlayerSnapshot` would have carried happily.
   */
  winnerName: z.string().max(64).nullable(),
  /**
   * Whether the winner was a bot, straight off the room's own bot bookkeeping.
   *
   * On this frame rather than read out of the `bots` channel for `ServerDied.killerIsBot`'s
   * reason, and it bites harder here: that channel names a player on the frame they come into
   * view, and most of the room reads this one from a spectator's seat having died some time ago.
   * False when there was no winner, which is the same thing `winnerName: null` already says.
   */
  winnerIsBot: z.boolean(),
  /**
   * **HOW THE ROUND FINISHED — the top few, best first.** Empty in every phase but `ended`, and
   * empty for a round that emptied.
   *
   * It is on the frame rather than derivable because the world cannot be asked afterwards: a
   * last-one-standing round ends with one body left and the boundary clears even that, and every
   * other finisher left the world when they were eaten. `MatchState.standings` is where the
   * recording is argued.
   *
   * Names, not ids — a bot can be on this list and has no account to point at, and nothing on the
   * receiving end looks a name up. Bounded by DEATHMATCH_STANDINGS, with each name under the same
   * 64 a player frame bounds one at.
   */
  standings: z
    .array(
      z.object({
        name: z.string().min(1).max(64),
        mass: z.number().int().nonnegative(),
        isBot: z.boolean(),
      }),
    )
    .max(DEATHMATCH_STANDINGS),
  /**
   * **"YOU WON THIS ROUND."** False on the shared payload and for everybody else — the one
   * per-viewer field, argued above.
   */
  youWon: z.boolean(),
});

export const serverDiedSchema = z.object({
  t: z.literal('died'),
  tick: tickSchema,
  /** Null when the player left the world without being eaten (disconnect, world edge, decay). */
  killerId: entityIdSchema.nullable(),
  killerName: z.string().max(64).nullable(),
  /**
   * Whether the killer was a bot, straight off the room's own bot bookkeeping.
   *
   * On this frame rather than read out of the `bots` channel because that channel is
   * interest-scoped: it names a player once, on the frame they come into view, so a killer the
   * budget culled is a killer the client has no flag for. False when there was no killer, which
   * is the same thing `killerName: null` already says.
   */
  killerIsBot: z.boolean(),
  /** Mass at time of death, for the death screen. */
  mass: massSchema,
  survivedMs: z.number().int().nonnegative(),
});

export const serverPongSchema = z.object({
  t: z.literal('pong'),
  /** Echoed verbatim from the ping so the client can compute RTT without keeping state. */
  clientTime: timestampSchema,
  serverTime: timestampSchema,
});

export const serverErrorSchema = z.object({
  t: z.literal('error'),
  code: protocolErrorCodeSchema,
  message: z.string().max(256),
});

export const serverMessageSchema = z.discriminatedUnion('t', [
  serverWelcomeSchema,
  serverSnapshotSchema,
  serverDeltaSchema,
  serverLeaderboardSchema,
  serverRosterSchema,
  serverNoticeSchema,
  serverVoteSchema,
  serverMatchSchema,
  serverDiedSchema,
  serverPongSchema,
  serverErrorSchema,
]);

export type ServerWelcome = z.infer<typeof serverWelcomeSchema>;
export type ServerSnapshot = z.infer<typeof serverSnapshotSchema>;
export type ServerDelta = z.infer<typeof serverDeltaSchema>;
export type ServerLeaderboard = z.infer<typeof serverLeaderboardSchema>;
export type ServerRoster = z.infer<typeof serverRosterSchema>;
export type ServerNotice = z.infer<typeof serverNoticeSchema>;
export type ServerVote = z.infer<typeof serverVoteSchema>;
export type ServerMatch = z.infer<typeof serverMatchSchema>;
export type ServerDied = z.infer<typeof serverDiedSchema>;
export type ServerPong = z.infer<typeof serverPongSchema>;
export type ServerError = z.infer<typeof serverErrorSchema>;
export type ServerMessage = z.infer<typeof serverMessageSchema>;

export type ClientMessageKind = ClientMessage['t'];
export type ServerMessageKind = ServerMessage['t'];
